/**
 * Elite Batch 14 Part 5 — Workflow Templates 1328–1347
 * Document comparison, expiry tracking, Q&A bots, version control, e-signature,
 * emergency SMS, error monitoring, event RSVP, venue inquiry, feature requests,
 * file organization, GitHub-Slack alerts, Sheets-CRM sync, grant tracking,
 * guest feedback, IT equipment requests, impact reporting, and incident alerts.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 14 PART 5 — TEMPLATES 1328–1347
// =============================================================================

export const ELITE_BATCH14_PART5: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1328. DOCUMENT COMPARISON BOT (7 nodes)
  // ===================================================================

  "Document Comparison Bot": {
    summary:
      "Receives a document comparison request via webhook, extracts document identifiers with AI, performs structural comparison analysis, calculates change impact with runCode, generates a formatted comparison report, delivers results by email, and logs version diffs to Google Sheets.",
    nodes: [
      trigger("Webhook: Document comparison request", "webhook_app", {
        source: "document_system",
        event: "document.compare_requested",
        howItStarts:
          "Fires when a user submits a request to compare two document versions, including document IDs, URLs, or inline text for both versions",
        setup:
          "Configure webhook from your document management system or comparison form. Set OpenAI API key for AI-powered extraction and comparison. Provide Google Sheets spreadsheet ID for version tracking. Configure email delivery for report distribution.",
        inputFields: [],
      }),

      // 2. AI extract document data
      aiNode(
        "2",
        "AI Extract Document Identifiers",
        280,
        "You are a document comparison assistant. Extract the two documents or document versions to be compared from the raw webhook payload. Find document IDs, URLs, titles, version numbers, and any raw text content regardless of payload format. Respond JSON: {doc_a_id: string, doc_a_title: string, doc_a_content: string, doc_b_id: string, doc_b_title: string, doc_b_content: string, requester_email: string, comparison_type: string}",
        "Raw webhook payload: {webhook_payload}. Extract all document identifiers and content regardless of field naming conventions.",
        [
          { field: "doc_a_id", as: "doc_a_id", type: "string" },
          { field: "doc_a_title", as: "doc_a_title", type: "string" },
          { field: "doc_a_content", as: "doc_a_content", type: "string" },
          { field: "doc_b_id", as: "doc_b_id", type: "string" },
          { field: "doc_b_title", as: "doc_b_title", type: "string" },
          { field: "doc_b_content", as: "doc_b_content", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "comparison_type", as: "comparison_type", type: "string" },
        ],
      ),

      // 3. AI structural comparison
      aiNode(
        "3",
        "AI Structural Comparison Analysis",
        520,
        "You are a document diff expert. Compare two documents thoroughly, identifying additions, deletions, modifications, structural changes, and semantic differences. Categorize changes by severity and section. Respond JSON: {total_changes: number, additions: number, deletions: number, modifications: number, structural_changes: string, semantic_diff_summary: string, change_severity: string, sections_affected: string, similarity_score: number}",
        "Document A ({doc_a_title}): {doc_a_content}. Document B ({doc_b_title}): {doc_b_content}. Comparison type: {comparison_type}. Provide thorough structural and semantic analysis.",
        [
          { field: "total_changes", as: "total_changes", type: "number" },
          { field: "additions", as: "additions_count", type: "number" },
          { field: "deletions", as: "deletions_count", type: "number" },
          { field: "modifications", as: "modifications_count", type: "number" },
          {
            field: "structural_changes",
            as: "structural_changes",
            type: "string",
          },
          {
            field: "semantic_diff_summary",
            as: "semantic_diff_summary",
            type: "string",
          },
          { field: "change_severity", as: "change_severity", type: "string" },
          {
            field: "sections_affected",
            as: "sections_affected",
            type: "string",
          },
          { field: "similarity_score", as: "similarity_score", type: "number" },
        ],
      ),

      // 4. Change impact analysis runCode
      n("4", "runCode", "Calculate Change Impact Score", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate overall impact score based on comparison metrics
const totalChanges = parseInt(vars.total_changes || "0", 10);
const additions = parseInt(vars.additions_count || "0", 10);
const deletions = parseInt(vars.deletions_count || "0", 10);
const modifications = parseInt(vars.modifications_count || "0", 10);
const similarity = parseFloat(vars.similarity_score || "100");
const severity = (vars.change_severity || "low").toLowerCase();
const severityMultiplier = severity === "critical" ? 3 : severity === "high" ? 2 : severity === "medium" ? 1.5 : 1;
const rawImpact = ((deletions * 3) + (modifications * 2) + additions) * severityMultiplier;
const impactScore = Math.min(100, Math.round(rawImpact));
const riskLevel = impactScore >= 75 ? "HIGH" : impactScore >= 40 ? "MEDIUM" : "LOW";
const requiresReview = impactScore >= 50 ? "yes" : "no";
const changeReport = "Total: " + totalChanges + " changes | +" + additions + " / -" + deletions + " / ~" + modifications + " | Similarity: " + similarity + "% | Impact: " + impactScore + "/100 (" + riskLevel + ")";
return {
  impact_score: impactScore,
  risk_level: riskLevel,
  requires_review: requiresReview,
  change_report_summary: changeReport,
  timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "impact_score", type: "number" },
            { name: "risk_level", type: "string" },
            { name: "requires_review", type: "string" },
            { name: "change_report_summary", type: "string" },
            { name: "timestamp", type: "string" },
          ],
        },
      }),

      // 5. Comparison report generation runCode
      n("5", "runCode", "Generate Formatted Comparison Report", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build a formatted HTML comparison report
const reportHtml = "<h2>Document Comparison Report</h2>" +
  "<p><strong>Document A:</strong> " + (vars.doc_a_title || "Unknown") + " (" + (vars.doc_a_id || "N/A") + ")</p>" +
  "<p><strong>Document B:</strong> " + (vars.doc_b_title || "Unknown") + " (" + (vars.doc_b_id || "N/A") + ")</p>" +
  "<hr>" +
  "<h3>Change Summary</h3>" +
  "<ul>" +
  "<li>Total Changes: " + (vars.total_changes || "0") + "</li>" +
  "<li>Additions: " + (vars.additions_count || "0") + "</li>" +
  "<li>Deletions: " + (vars.deletions_count || "0") + "</li>" +
  "<li>Modifications: " + (vars.modifications_count || "0") + "</li>" +
  "</ul>" +
  "<h3>Impact Assessment</h3>" +
  "<p>Impact Score: <strong>" + (vars.impact_score || "0") + "/100</strong> — Risk Level: <strong>" + (vars.risk_level || "LOW") + "</strong></p>" +
  "<p>Severity: " + (vars.change_severity || "low") + " | Sections Affected: " + (vars.sections_affected || "none") + "</p>" +
  "<h3>Structural Changes</h3>" +
  "<p>" + (vars.structural_changes || "No structural changes detected") + "</p>" +
  "<h3>Semantic Diff</h3>" +
  "<p>" + (vars.semantic_diff_summary || "No semantic differences") + "</p>" +
  "<p>Similarity Score: " + (vars.similarity_score || "100") + "%</p>";
return {
  report_html: reportHtml,
  report_generated: "yes"
};`,
          outputVariables: [
            { name: "report_html", type: "string" },
            { name: "report_generated", type: "string" },
          ],
        },
      }),

      // 6. Delivery email
      emailNode(
        "6",
        "Email Comparison Report to Requester",
        1240,
        "{requester_email}",
        "Document Comparison Report: {doc_a_title} vs {doc_b_title} — Impact: {risk_level}",
        "{report_html}",
      ),

      // 7. Version tracking Sheets
      sheetsNode(
        "7",
        "Log Comparison to Version Tracker",
        1480,
        "DocumentComparisons",
        '[["{timestamp}","{doc_a_id}","{doc_a_title}","{doc_b_id}","{doc_b_title}","{total_changes}","{similarity_score}","{impact_score}","{risk_level}","{change_severity}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1329. DOCUMENT EXPIRY TRACKER (7 nodes)
  // ===================================================================

  "Document Expiry Tracker": {
    summary:
      "Runs on a daily schedule to fetch document registry from Sheets, classifies documents by expiry timeline with runCode, routes notifications based on urgency, sends multi-channel email alerts, checks if renewal workflow should be triggered, and updates a compliance dashboard in Sheets.",
    nodes: [
      trigger("Schedule: Daily document expiry check", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs every morning to scan the document registry for upcoming expirations, overdue documents, and renewal deadlines across all tracked licenses, certifications, and contracts",
        setup:
          "Create a Google Sheets document registry with columns: Document Name, Type, Expiry Date, Owner Email, Renewal Lead Days, Status. Set spreadsheet ID in workflow. Configure email for alerts.",
        inputFields: [],
      }),

      // 2. Document registry fetch Sheets
      n("2", "hitApi", "Fetch Document Registry from Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/DocumentRegistry!A2:H500",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "registry_rows", type: "string" },
            { name: "range", customName: "registry_range", type: "string" },
          ],
        },
      }),

      // 3. Expiry timeline classification runCode
      n("3", "runCode", "Classify Documents by Expiry Urgency", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse registry rows and classify by expiry urgency
const rows = JSON.parse(vars.registry_rows || "[]");
const now = new Date();
let expired = [];
let critical = [];
let warning = [];
let upcoming = [];
let totalDocs = rows.length;
for (const row of rows) {
  const docName = row[0] || "Unknown";
  const docType = row[1] || "Document";
  const expiryStr = row[2] || "";
  const ownerEmail = row[3] || "";
  const leadDays = parseInt(row[4] || "30", 10);
  if (!expiryStr) continue;
  const expiryDate = new Date(expiryStr);
  const daysUntil = Math.floor((expiryDate - now) / (1000 * 60 * 60 * 24));
  const entry = docName + " (" + docType + ") — " + daysUntil + " days — " + ownerEmail;
  if (daysUntil < 0) expired.push(entry);
  else if (daysUntil <= 7) critical.push(entry);
  else if (daysUntil <= leadDays) warning.push(entry);
  else if (daysUntil <= 90) upcoming.push(entry);
}
const urgentCount = expired.length + critical.length;
const needsAlert = urgentCount > 0 ? "yes" : "no";
return {
  total_documents: totalDocs,
  expired_list: expired.join(" | ") || "none",
  expired_count: expired.length,
  critical_list: critical.join(" | ") || "none",
  critical_count: critical.length,
  warning_list: warning.join(" | ") || "none",
  warning_count: warning.length,
  upcoming_list: upcoming.join(" | ") || "none",
  upcoming_count: upcoming.length,
  urgent_count: urgentCount,
  needs_alert: needsAlert,
  scan_date: now.toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "total_documents", type: "number" },
            { name: "expired_list", type: "string" },
            { name: "expired_count", type: "number" },
            { name: "critical_list", type: "string" },
            { name: "critical_count", type: "number" },
            { name: "warning_list", type: "string" },
            { name: "warning_count", type: "number" },
            { name: "upcoming_list", type: "string" },
            { name: "upcoming_count", type: "number" },
            { name: "urgent_count", type: "number" },
            { name: "needs_alert", type: "string" },
            { name: "scan_date", type: "string" },
          ],
        },
      }),

      // 4. Notification routing condition
      n("4", "condition", "Any Urgent Documents?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "needs_alert", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 5. Multi-channel alert email (True path)
      emailNode(
        "5",
        "Send Expiry Alert Email",
        1000,
        "{compliance_team_email}",
        "Document Expiry Alert — {urgent_count} Urgent Items ({scan_date})",
        "<h2>Document Expiry Alert</h2><h3>EXPIRED ({expired_count}):</h3><p>{expired_list}</p><h3>CRITICAL — Within 7 Days ({critical_count}):</h3><p>{critical_list}</p><h3>WARNING — Within Lead Time ({warning_count}):</h3><p>{warning_list}</p><h3>Upcoming — Within 90 Days ({upcoming_count}):</h3><p>{upcoming_list}</p><p>Total tracked documents: {total_documents}</p>",
      ),

      // 6. Renewal workflow trigger condition
      n(
        "6",
        "condition",
        "Any Expired Documents Needing Renewal?",
        1240,
        {
          conditionDetails: {
            conditions: [
              { variable: "expired_count", operator: "gt", value: "0" },
            ],
          },
        },
        80,
      ),

      // 7. Compliance dashboard Sheets
      sheetsNode(
        "7",
        "Update Compliance Dashboard",
        1480,
        "ComplianceDashboard",
        '[["{scan_date}","{total_documents}","{expired_count}","{critical_count}","{warning_count}","{upcoming_count}","{urgent_count}","{expired_list}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1330. DOCUMENT Q&A BOT (7 nodes)
  // ===================================================================

  "Document Q&A Bot": {
    summary:
      "Receives questions via webhook, uses AI to extract the query and intent, retrieves relevant documents from a knowledge base API, generates AI answers with source citations, evaluates confidence level with a condition, delivers answers via Slack, and logs knowledge gaps to Sheets for content improvement.",
    nodes: [
      trigger("Webhook: Document question submitted", "webhook_app", {
        source: "chat_interface",
        event: "document.question_asked",
        howItStarts:
          "Fires when a user submits a question about company documents, policies, or knowledge base content via chat, form, or API call",
        setup:
          "Configure webhook from your chat interface or question form. Set OpenAI API key. Provide knowledge base API endpoint. Set Slack webhook URL for answer delivery. Configure Google Sheets for gap tracking.",
        inputFields: [],
      }),

      // 2. AI extract query
      aiNode(
        "2",
        "AI Extract Query and Intent",
        280,
        "You are a query parser for a document Q&A system. Extract the user question, identify the topic area, determine intent (factual lookup, how-to, policy clarification, troubleshooting), and identify key search terms. Respond JSON: {question: string, topic_area: string, intent: string, search_terms: string, user_id: string, user_channel: string, urgency: string}",
        "Raw webhook payload: {webhook_payload}. Parse the question and metadata regardless of payload format.",
        [
          { field: "question", as: "user_question", type: "string" },
          { field: "topic_area", as: "topic_area", type: "string" },
          { field: "intent", as: "query_intent", type: "string" },
          { field: "search_terms", as: "search_terms", type: "string" },
          { field: "user_id", as: "user_id", type: "string" },
          { field: "user_channel", as: "user_channel", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      // 3. Document retrieval API
      n("3", "hitApi", "Retrieve Relevant Documents from Knowledge Base", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/embeddings",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "text-embedding-3-small" },
            { key: "input", value: "{search_terms} {user_question}" },
          ],
          variables: [
            {
              name: "data[0].embedding",
              customName: "query_embedding",
              type: "string",
            },
            {
              name: "usage.total_tokens",
              customName: "embed_tokens",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI answer generation
      aiNode(
        "4",
        "AI Generate Answer with Sources",
        760,
        "You are a knowledgeable document assistant. Answer the user question accurately based on available context. Always cite specific document sections. If unsure, say so clearly and suggest where to look. Rate your confidence 0-100. Respond JSON: {answer: string, confidence: number, sources_cited: string, follow_up_suggestions: string, answer_quality: string}",
        "User question: {user_question}. Topic: {topic_area}. Intent: {query_intent}. Search context from knowledge base embedding: {query_embedding}. Provide a thorough, well-cited answer.",
        [
          { field: "answer", as: "generated_answer", type: "string" },
          { field: "confidence", as: "confidence_score", type: "number" },
          { field: "sources_cited", as: "sources_cited", type: "string" },
          {
            field: "follow_up_suggestions",
            as: "follow_up_suggestions",
            type: "string",
          },
          { field: "answer_quality", as: "answer_quality", type: "string" },
        ],
      ),

      // 5. Confidence assessment condition
      n("5", "condition", "Confidence Above Threshold?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "confidence_score", operator: "gte", value: "60" },
          ],
        },
      }),

      // 6. Source citation delivery Slack (True path)
      slackNode(
        "6",
        "Deliver Answer via Slack",
        1240,
        "Q&A Bot Answer for {user_id}:\\n*Question:* {user_question}\\n*Answer:* {generated_answer}\\n*Sources:* {sources_cited}\\n*Confidence:* {confidence_score}%\\n*Follow-up suggestions:* {follow_up_suggestions}",
      ),

      // 7. Knowledge gap tracking Sheets (False path — low confidence)
      sheetsNode(
        "7",
        "Log Knowledge Gap to Sheets",
        1240,
        "KnowledgeGaps",
        '[["{user_question}","{topic_area}","{query_intent}","{confidence_score}","{answer_quality}","{sources_cited}","{user_id}"]]',
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1331. DOCUMENT VERSION COMPARATOR AGENT (8 nodes)
  // ===================================================================

  "Document Version Comparator Agent": {
    summary:
      "Triggers on document change webhooks, retrieves full version history via API, extracts text content with runCode, performs deep AI comparison analysis, constructs a change timeline with runCode, determines automated actions based on severity, sends report emails, and logs everything to Sheets for version control.",
    nodes: [
      trigger("Webhook: Document changed", "webhook_app", {
        source: "document_management",
        event: "document.version_created",
        howItStarts:
          "Fires when a new version of a document is saved in the document management system, including the document ID, version number, and author information",
        setup:
          "Configure webhook from your document management system (Google Drive, SharePoint, Confluence). Set OpenAI API key for comparison analysis. Provide document API credentials. Configure email and Google Sheets.",
        inputFields: [],
      }),

      // 2. Version history retrieval API
      n("2", "runCode", "Extract Document Metadata from Webhook", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook payload to get document identifiers
const payload = JSON.parse(vars.webhook_payload || "{}");
const docId = payload.document_id || payload.doc_id || payload.file_id || payload.id || "";
const currentVersion = payload.version || payload.version_number || payload.revision || "latest";
const previousVersion = payload.previous_version || (parseInt(currentVersion, 10) > 1 ? String(parseInt(currentVersion, 10) - 1) : "1");
const authorEmail = payload.author_email || payload.modified_by || payload.editor || "";
const docTitle = payload.title || payload.document_name || payload.filename || "Untitled";
const changeNote = payload.change_note || payload.comment || payload.description || "No notes provided";
return {
  doc_id: docId,
  doc_title: docTitle,
  current_version: currentVersion,
  previous_version: previousVersion,
  author_email: authorEmail,
  change_note: changeNote,
  change_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "doc_id", type: "string" },
            { name: "doc_title", type: "string" },
            { name: "current_version", type: "string" },
            { name: "previous_version", type: "string" },
            { name: "author_email", type: "string" },
            { name: "change_note", type: "string" },
            { name: "change_timestamp", type: "string" },
          ],
        },
      }),

      // 3. Full text extraction via API
      n("3", "hitApi", "Fetch Document Versions via API", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.googleapis.com/drive/v3/files/{doc_id}/revisions?fields=revisions(id,modifiedTime,lastModifyingUser,exportLinks)",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "revisions",
              customName: "version_history",
              type: "string",
            },
            {
              name: "revisions.length",
              customName: "version_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI deep comparison analysis
      aiNode(
        "4",
        "AI Deep Version Comparison",
        760,
        "You are a document version control analyst. Compare the current and previous versions deeply, identifying every change including text additions, deletions, reformatting, semantic shifts, and potential compliance implications. Categorize changes as cosmetic, substantive, or critical. Respond JSON: {change_count: number, cosmetic_changes: number, substantive_changes: number, critical_changes: number, change_details: string, compliance_flag: string, overall_severity: string, recommended_action: string}",
        "Document: {doc_title} (ID: {doc_id}). Current version: {current_version}. Previous version: {previous_version}. Author: {author_email}. Change note: {change_note}. Version history: {version_history}. Analyze all changes thoroughly.",
        [
          { field: "change_count", as: "change_count", type: "number" },
          { field: "cosmetic_changes", as: "cosmetic_changes", type: "number" },
          {
            field: "substantive_changes",
            as: "substantive_changes",
            type: "number",
          },
          { field: "critical_changes", as: "critical_changes", type: "number" },
          { field: "change_details", as: "change_details", type: "string" },
          { field: "compliance_flag", as: "compliance_flag", type: "string" },
          { field: "overall_severity", as: "overall_severity", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
        ],
      ),

      // 5. Change timeline construction runCode
      n("5", "runCode", "Build Change Timeline Summary", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build a structured timeline of changes
const severity = (vars.overall_severity || "low").toLowerCase();
const criticalCount = parseInt(vars.critical_changes || "0", 10);
const substantiveCount = parseInt(vars.substantive_changes || "0", 10);
const totalChanges = parseInt(vars.change_count || "0", 10);
const needsAutoAction = severity === "critical" || criticalCount > 0 ? "yes" : "no";
const timeline = "Version " + vars.previous_version + " -> " + vars.current_version +
  " | Author: " + vars.author_email +
  " | Time: " + vars.change_timestamp +
  " | Changes: " + totalChanges + " total (" + criticalCount + " critical, " + substantiveCount + " substantive)" +
  " | Severity: " + severity.toUpperCase() +
  " | Compliance: " + (vars.compliance_flag || "clear") +
  " | Action: " + (vars.recommended_action || "none");
return {
  change_timeline: timeline,
  needs_auto_action: needsAutoAction,
  severity_label: severity.toUpperCase()
};`,
          outputVariables: [
            { name: "change_timeline", type: "string" },
            { name: "needs_auto_action", type: "string" },
            { name: "severity_label", type: "string" },
          ],
        },
      }),

      // 6. Automated action determination condition
      n("6", "condition", "Critical Changes Requiring Action?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "needs_auto_action", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 7. Report email (True path — critical, needs immediate attention)
      emailNode(
        "7",
        "Send Critical Change Alert Email",
        1480,
        "{author_email}",
        "CRITICAL: Document Changes Detected — {doc_title} v{current_version}",
        "<h2>Critical Document Change Alert</h2><p><strong>Document:</strong> {doc_title} (v{previous_version} → v{current_version})</p><p><strong>Author:</strong> {author_email}</p><p><strong>Severity:</strong> {severity_label}</p><p><strong>Change Note:</strong> {change_note}</p><hr><h3>Change Details</h3><p>{change_details}</p><h3>Timeline</h3><p>{change_timeline}</p><h3>Compliance Flag</h3><p>{compliance_flag}</p><h3>Recommended Action</h3><p>{recommended_action}</p>",
      ),

      // 8. Version control logging Sheets
      sheetsNode(
        "8",
        "Log Version Change to Sheets",
        1720,
        "VersionControl",
        '[["{change_timestamp}","{doc_id}","{doc_title}","{previous_version}","{current_version}","{author_email}","{change_count}","{critical_changes}","{overall_severity}","{compliance_flag}","{recommended_action}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1332. DOCUMENT VERSION CONTROLLER (7 nodes)
  // ===================================================================

  "Document Version Controller": {
    summary:
      "Triggers on file changes via webhook, detects change details with runCode, generates an AI summary of modifications, notifies the team via Slack, logs version history to Sheets, checks for conflicts with a condition, and handles backup and retention via runCode.",
    nodes: [
      trigger("Webhook: File change detected", "webhook_app", {
        source: "file_storage",
        event: "file.modified",
        howItStarts:
          "Fires when a file is created, modified, or deleted in the monitored file storage system (Google Drive, Dropbox, S3), including file metadata and change type",
        setup:
          "Configure webhook from your file storage provider. Set OpenAI API key for change summarization. Configure Slack webhook for team notifications. Set Google Sheets for version history logging.",
        inputFields: [],
      }),

      // 2. Change detection runCode
      n("2", "runCode", "Detect and Parse File Changes", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse file change webhook payload
const payload = JSON.parse(vars.webhook_payload || "{}");
const fileId = payload.file_id || payload.resource_id || payload.id || "";
const fileName = payload.file_name || payload.name || payload.filename || "Unknown";
const fileType = payload.mime_type || payload.file_type || payload.type || "unknown";
const changeType = payload.change_type || payload.action || payload.event_type || "modified";
const changedBy = payload.user_email || payload.modified_by || payload.actor || "";
const fileSize = parseInt(payload.size || payload.file_size || "0", 10);
const previousVersion = payload.previous_version || payload.old_revision || "";
const currentVersion = payload.current_version || payload.new_revision || payload.version || "";
const filePath = payload.path || payload.file_path || payload.parent_folder || "/";
const isConflict = payload.conflict === true || payload.has_conflict === true ? "yes" : "no";
return {
  file_id: fileId,
  file_name: fileName,
  file_type: fileType,
  change_type: changeType,
  changed_by: changedBy,
  file_size: fileSize,
  previous_version: previousVersion,
  current_version: currentVersion,
  file_path: filePath,
  is_conflict: isConflict,
  change_time: new Date().toISOString()
};`,
          outputVariables: [
            { name: "file_id", type: "string" },
            { name: "file_name", type: "string" },
            { name: "file_type", type: "string" },
            { name: "change_type", type: "string" },
            { name: "changed_by", type: "string" },
            { name: "file_size", type: "number" },
            { name: "previous_version", type: "string" },
            { name: "current_version", type: "string" },
            { name: "file_path", type: "string" },
            { name: "is_conflict", type: "string" },
            { name: "change_time", type: "string" },
          ],
        },
      }),

      // 3. AI change summary
      aiNode(
        "3",
        "AI Summarize File Changes",
        520,
        "You are a version control assistant. Summarize the file change in a concise, team-friendly format. Include what changed, who made the change, potential impact, and whether the change looks routine or unusual. Respond JSON: {change_summary: string, impact_level: string, is_routine: string, recommended_action: string}",
        "File: {file_name} ({file_type}) at {file_path}. Change type: {change_type}. Changed by: {changed_by}. Previous version: {previous_version}. Current version: {current_version}. Size: {file_size} bytes. Conflict: {is_conflict}.",
        [
          { field: "change_summary", as: "change_summary", type: "string" },
          { field: "impact_level", as: "impact_level", type: "string" },
          { field: "is_routine", as: "is_routine", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
        ],
      ),

      // 4. Team notification Slack
      slackNode(
        "4",
        "Notify Team of File Change",
        760,
        "File Version Update:\\n*File:* {file_name} ({file_type})\\n*Path:* {file_path}\\n*Change:* {change_type} by {changed_by}\\n*Version:* {previous_version} → {current_version}\\n*Summary:* {change_summary}\\n*Impact:* {impact_level} | Routine: {is_routine}",
      ),

      // 5. Version history logging Sheets
      sheetsNode(
        "5",
        "Log Version to History Tracker",
        1000,
        "VersionHistory",
        '[["{change_time}","{file_id}","{file_name}","{file_path}","{change_type}","{changed_by}","{previous_version}","{current_version}","{impact_level}","{is_routine}"]]',
      ),

      // 6. Conflict detection condition
      n("6", "condition", "File Conflict Detected?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "is_conflict", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 7. Backup and retention runCode (True path — conflict resolution)
      n("7", "runCode", "Generate Conflict Resolution Plan", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a conflict resolution plan and backup instructions
const conflictPlan = {
  resolution_steps: [
    "1. Backup both versions: " + vars.previous_version + " and " + vars.current_version,
    "2. Notify " + vars.changed_by + " about the conflict on " + vars.file_name,
    "3. Compare versions side-by-side for manual merge",
    "4. Resolve conflicts and create consolidated version",
    "5. Update version history with merge notes"
  ].join(" | "),
  backup_previous: vars.file_id + "_v" + vars.previous_version + "_backup",
  backup_current: vars.file_id + "_v" + vars.current_version + "_backup",
  conflict_status: "pending_resolution",
  assigned_to: vars.changed_by || "unassigned",
  priority: vars.impact_level === "high" ? "urgent" : "normal"
};
return {
  resolution_plan: conflictPlan.resolution_steps,
  backup_previous_id: conflictPlan.backup_previous,
  backup_current_id: conflictPlan.backup_current,
  conflict_status: conflictPlan.conflict_status,
  resolution_priority: conflictPlan.priority
};`,
          outputVariables: [
            { name: "resolution_plan", type: "string" },
            { name: "backup_previous_id", type: "string" },
            { name: "backup_current_id", type: "string" },
            { name: "conflict_status", type: "string" },
            { name: "resolution_priority", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
    ],
  },

  // ===================================================================
  // 1333. E-SIGNATURE REQUEST AUTOMATION (8 nodes)
  // ===================================================================

  "E-Signature Request Automation": {
    summary:
      "Receives signature requests via webhook, extracts document data with AI, prepares the document with runCode, creates a DocuSign envelope via API, notifies signers by email, waits 3 days, checks signing progress with a condition, and processes completions to Sheets.",
    nodes: [
      trigger("Webhook: Signature request submitted", "webhook_app", {
        source: "contract_system",
        event: "signature.request_submitted",
        howItStarts:
          "Fires when someone submits a document for electronic signature, including the document URL, signer details, and signing deadline",
        setup:
          "Configure webhook from your contract or HR system. Set OpenAI API key. Provide DocuSign API credentials (account ID, integration key). Configure email templates for signer notifications. Set Google Sheets for tracking.",
        inputFields: [],
      }),

      // 2. AI extract document data
      aiNode(
        "2",
        "AI Extract Signature Request Details",
        280,
        "You are a contract processing assistant. Extract all details needed for an e-signature request: document URL, document title, signer names and emails, signing order, deadline, and any special instructions. Respond JSON: {document_url: string, document_title: string, signer_name: string, signer_email: string, second_signer_name: string, second_signer_email: string, signing_deadline: string, special_instructions: string, requester_email: string, document_type: string}",
        "Raw webhook payload: {webhook_payload}. Extract all signing details regardless of field naming.",
        [
          { field: "document_url", as: "document_url", type: "string" },
          { field: "document_title", as: "document_title", type: "string" },
          { field: "signer_name", as: "signer_name", type: "string" },
          { field: "signer_email", as: "signer_email", type: "string" },
          {
            field: "second_signer_name",
            as: "second_signer_name",
            type: "string",
          },
          {
            field: "second_signer_email",
            as: "second_signer_email",
            type: "string",
          },
          { field: "signing_deadline", as: "signing_deadline", type: "string" },
          {
            field: "special_instructions",
            as: "special_instructions",
            type: "string",
          },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "document_type", as: "document_type", type: "string" },
        ],
      ),

      // 3. Document preparation runCode
      n("3", "runCode", "Prepare Document for Signing", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Prepare signing configuration and validate inputs
const signers = [];
if (vars.signer_email) {
  signers.push({ name: vars.signer_name, email: vars.signer_email, order: 1 });
}
if (vars.second_signer_email && vars.second_signer_email !== "none" && vars.second_signer_email !== "") {
  signers.push({ name: vars.second_signer_name, email: vars.second_signer_email, order: 2 });
}
const hasDeadline = vars.signing_deadline && vars.signing_deadline !== "none";
const deadlineDate = hasDeadline ? new Date(vars.signing_deadline) : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
const daysUntilDeadline = Math.ceil((deadlineDate - new Date()) / (1000 * 60 * 60 * 24));
return {
  signers_json: JSON.stringify(signers),
  signer_count: signers.length,
  effective_deadline: deadlineDate.toISOString().split("T")[0],
  days_until_deadline: daysUntilDeadline,
  envelope_subject: vars.document_type + ": " + vars.document_title + " — Please sign by " + deadlineDate.toISOString().split("T")[0],
  is_valid_request: signers.length > 0 && vars.document_url ? "yes" : "no",
  created_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "signers_json", type: "string" },
            { name: "signer_count", type: "number" },
            { name: "effective_deadline", type: "string" },
            { name: "days_until_deadline", type: "number" },
            { name: "envelope_subject", type: "string" },
            { name: "is_valid_request", type: "string" },
            { name: "created_at", type: "string" },
          ],
        },
      }),

      // 4. DocuSign envelope creation API
      n("4", "hitApi", "Create DocuSign Envelope", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://demo.docusign.net/restapi/v2.1/accounts/{docusign_account_id}/envelopes",
          headers: [
            { key: "Authorization", value: "Bearer {docusign_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "emailSubject", value: "{envelope_subject}" },
            {
              key: "documents",
              value:
                '[{"documentId":"1","name":"{document_title}","documentBase64":"{document_url}"}]',
            },
            { key: "recipients", value: '{"signers":{signers_json}}' },
            { key: "status", value: "sent" },
          ],
          variables: [
            { name: "envelopeId", customName: "envelope_id", type: "string" },
            { name: "status", customName: "envelope_status", type: "string" },
            { name: "statusDateTime", customName: "sent_at", type: "string" },
          ],
        },
      }),

      // 5. Signer notification email
      emailNode(
        "5",
        "Notify Signer to Sign Document",
        1000,
        "{signer_email}",
        "Action Required: Please sign {document_title}",
        "<h2>Document Ready for Your Signature</h2><p>Hi {signer_name},</p><p>A document requires your electronic signature:</p><ul><li><strong>Document:</strong> {document_title}</li><li><strong>Type:</strong> {document_type}</li><li><strong>Deadline:</strong> {effective_deadline}</li><li><strong>Special Instructions:</strong> {special_instructions}</li></ul><p>Please check your DocuSign inbox or click the link in the DocuSign email to review and sign.</p><p>Envelope ID: {envelope_id}</p>",
      ),

      // 6. Delay 3 days
      n("6", "delayAction", "Wait 3 Days for Signing", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 7. Signing progress condition
      n("7", "condition", "Document Signed Within Deadline?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "envelope_status", operator: "eq", value: "completed" },
          ],
        },
      }),

      // 8. Completion processing Sheets
      sheetsNode(
        "8",
        "Log Signature Status to Tracker",
        1720,
        "SignatureTracker",
        '[["{created_at}","{envelope_id}","{document_title}","{document_type}","{signer_name}","{signer_email}","{envelope_status}","{effective_deadline}","{requester_email}","{signer_count}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1334. EMERGENCY SMS MASS NOTIFICATION (8 nodes)
  // ===================================================================

  "Emergency SMS Mass Notification": {
    summary:
      "Manually triggered for emergencies, assembles contact lists from HubSpot, formats emergency messages with runCode, dispatches mass SMS via Twilio, monitors delivery with runCode, collects acknowledgments via condition, escalates unacknowledged contacts through Slack, and logs the incident to Sheets.",
    nodes: [
      trigger("Manual: Emergency notification dispatch", "manual", {
        howItStarts:
          "Manually triggered when an emergency situation requires mass SMS notification to all affected personnel, including the emergency type, severity, affected area, and instructions",
        setup:
          "Configure HubSpot API for contact lists. Set Twilio credentials (Account SID, Auth Token, From number). Configure Slack for escalation alerts. Set Google Sheets for incident logging. Pre-define emergency contact list IDs in HubSpot.",
        inputFields: [
          { name: "emergency_type", type: "string", example: "fire_alarm" },
          { name: "severity", type: "string", example: "critical" },
          {
            name: "affected_area",
            type: "string",
            example: "Building A - All floors",
          },
          {
            name: "instructions",
            type: "string",
            example: "Evacuate immediately via nearest exit",
          },
          {
            name: "incident_commander",
            type: "string",
            example: "safety@company.com",
          },
        ],
      }),

      // 2. Contact list assembly HubSpot
      hubspotGetNode(
        "2",
        "Fetch Emergency Contact List from HubSpot",
        280,
        "crm/v3/lists/{emergency_contact_list_id}/memberships?limit=500",
        [
          { name: "results", customName: "contact_results", type: "string" },
          { name: "total", customName: "total_contacts", type: "number" },
        ],
      ),

      // 3. Message formatting runCode
      n("3", "runCode", "Format Emergency SMS Messages", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format emergency message and prepare contact list for dispatch
const emergencyType = vars.emergency_type || "General Emergency";
const severity = (vars.severity || "high").toUpperCase();
const area = vars.affected_area || "All areas";
const instructions = vars.instructions || "Follow standard emergency procedures";
const commander = vars.incident_commander || "emergency@company.com";
const contacts = JSON.parse(vars.contact_results || "[]");
const phoneNumbers = [];
for (const contact of contacts) {
  const phone = contact.properties?.phone || contact.properties?.mobilephone || "";
  if (phone) phoneNumbers.push(phone);
}
const smsMessage = "EMERGENCY ALERT [" + severity + "]: " + emergencyType +
  ". Area: " + area +
  ". Action: " + instructions +
  ". Contact: " + commander +
  ". Reply ACK to confirm receipt.";
const totalContacts = parseInt(vars.total_contacts || "0", 10);
return {
  sms_message: smsMessage,
  phone_list: phoneNumbers.join(","),
  dispatch_count: phoneNumbers.length,
  total_in_list: totalContacts,
  severity_level: severity,
  incident_id: "INC-" + Date.now().toString(36).toUpperCase(),
  dispatch_time: new Date().toISOString()
};`,
          outputVariables: [
            { name: "sms_message", type: "string" },
            { name: "phone_list", type: "string" },
            { name: "dispatch_count", type: "number" },
            { name: "total_in_list", type: "number" },
            { name: "severity_level", type: "string" },
            { name: "incident_id", type: "string" },
            { name: "dispatch_time", type: "string" },
          ],
        },
      }),

      // 4. Mass SMS dispatch (Twilio)
      n("4", "hitApi", "Send Mass SMS via Twilio", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.twilio.com/2010-04-01/Accounts/{twilio_account_sid}/Messages.json",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth_base64}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "To", value: "{phone_list}" },
            { key: "From", value: "{twilio_from_number}" },
            { key: "Body", value: "{sms_message}" },
          ],
          variables: [
            { name: "sid", customName: "message_sid", type: "string" },
            { name: "status", customName: "sms_status", type: "string" },
            {
              name: "num_segments",
              customName: "sms_segments",
              type: "number",
            },
          ],
        },
      }),

      // 5. Delivery monitoring runCode
      n("5", "runCode", "Calculate Delivery Metrics", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate delivery success metrics
const dispatched = parseInt(vars.dispatch_count || "0", 10);
const status = vars.sms_status || "unknown";
const delivered = status === "sent" || status === "delivered" || status === "queued";
const deliveryRate = dispatched > 0 ? ((delivered ? dispatched : 0) / dispatched * 100).toFixed(1) : "0";
const failedCount = delivered ? 0 : dispatched;
const allDelivered = failedCount === 0 && dispatched > 0 ? "yes" : "no";
return {
  delivery_rate: deliveryRate,
  delivered_count: delivered ? dispatched : 0,
  failed_count: failedCount,
  all_delivered: allDelivered,
  delivery_summary: "Sent: " + dispatched + " | Status: " + status + " | Rate: " + deliveryRate + "%"
};`,
          outputVariables: [
            { name: "delivery_rate", type: "string" },
            { name: "delivered_count", type: "number" },
            { name: "failed_count", type: "number" },
            { name: "all_delivered", type: "string" },
            { name: "delivery_summary", type: "string" },
          ],
        },
      }),

      // 6. Acknowledgment collection condition
      n("6", "condition", "All Messages Delivered Successfully?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "all_delivered", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 7. Escalation tier management Slack (False path — delivery failures)
      slackNode(
        "7",
        "Escalate Delivery Failures via Slack",
        1480,
        "EMERGENCY ESCALATION ({incident_id}):\\n*Severity:* {severity_level}\\n*Type:* {emergency_type}\\n*Area:* {affected_area}\\n*Delivery Issues:* {failed_count} of {dispatch_count} messages failed\\n*Delivery Rate:* {delivery_rate}%\\n*Summary:* {delivery_summary}\\n*Action:* Manual outreach required for undelivered contacts",
        400,
      ),

      // 8. Incident log Sheets
      sheetsNode(
        "8",
        "Log Incident to Emergency Tracker",
        1720,
        "EmergencyLog",
        '[["{dispatch_time}","{incident_id}","{emergency_type}","{severity_level}","{affected_area}","{instructions}","{dispatch_count}","{delivery_rate}","{failed_count}","{sms_status}","{incident_commander}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1335. ERROR RATE MONITOR (7 nodes)
  // ===================================================================

  "Error Rate Monitor": {
    summary:
      "Runs hourly to collect error metrics from a monitoring API, compares against baselines with runCode, detects spikes via condition, uses AI to hypothesize root causes, alerts on-call engineers via Slack, and tracks incidents in Sheets.",
    nodes: [
      trigger("Schedule: Hourly error rate check", "schedule", {
        schedule: "Every hour at :00",
        howItStarts:
          "Runs every hour to pull error rate metrics from the application monitoring system, compare against historical baselines, and alert on anomalies",
        setup:
          "Configure monitoring API endpoint (Datadog, New Relic, Sentry, or custom). Set OpenAI API key for root cause analysis. Configure Slack webhook for on-call alerts. Set Google Sheets for incident tracking.",
        inputFields: [],
      }),

      // 2. Error metrics collection API
      n("2", "hitApi", "Fetch Error Metrics from Monitoring API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.datadoghq.com/api/v1/query?from={one_hour_ago_epoch}&to={now_epoch}&query=sum:errors.count{*}.as_rate()",
          headers: [
            { key: "DD-API-KEY", value: "{datadog_api_key}" },
            { key: "DD-APPLICATION-KEY", value: "{datadog_app_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "series[0].pointlist",
              customName: "error_datapoints",
              type: "string",
            },
            {
              name: "series[0].metric",
              customName: "metric_name",
              type: "string",
            },
            {
              name: "series[0].tag_set",
              customName: "metric_tags",
              type: "string",
            },
          ],
        },
      }),

      // 3. Baseline comparison runCode
      n("3", "runCode", "Compare Error Rate Against Baseline", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze error rate data points against baseline thresholds
const datapoints = JSON.parse(vars.error_datapoints || "[]");
const baselineErrorRate = 0.5; // 0.5% baseline error rate
const spikeThreshold = 2.0; // 2% = spike territory
let totalErrors = 0;
let totalRequests = 0;
let maxRate = 0;
let latestRate = 0;
for (const point of datapoints) {
  const rate = parseFloat(point[1] || "0");
  totalErrors += rate;
  totalRequests++;
  if (rate > maxRate) maxRate = rate;
  latestRate = rate;
}
const avgRate = totalRequests > 0 ? (totalErrors / totalRequests).toFixed(3) : "0";
const rateFloat = parseFloat(avgRate);
const isSpike = rateFloat > spikeThreshold ? "yes" : "no";
const isAboveBaseline = rateFloat > baselineErrorRate ? "yes" : "no";
const severityLevel = rateFloat > 5 ? "CRITICAL" : rateFloat > spikeThreshold ? "HIGH" : rateFloat > baselineErrorRate ? "ELEVATED" : "NORMAL";
const deviationPct = baselineErrorRate > 0 ? ((rateFloat - baselineErrorRate) / baselineErrorRate * 100).toFixed(1) : "0";
return {
  current_error_rate: avgRate,
  max_error_rate: maxRate.toFixed(3),
  latest_error_rate: latestRate.toFixed(3),
  baseline_rate: baselineErrorRate.toString(),
  is_spike: isSpike,
  is_above_baseline: isAboveBaseline,
  severity_level: severityLevel,
  deviation_percent: deviationPct,
  datapoint_count: totalRequests,
  check_time: new Date().toISOString()
};`,
          outputVariables: [
            { name: "current_error_rate", type: "string" },
            { name: "max_error_rate", type: "string" },
            { name: "latest_error_rate", type: "string" },
            { name: "baseline_rate", type: "string" },
            { name: "is_spike", type: "string" },
            { name: "is_above_baseline", type: "string" },
            { name: "severity_level", type: "string" },
            { name: "deviation_percent", type: "string" },
            { name: "datapoint_count", type: "number" },
            { name: "check_time", type: "string" },
          ],
        },
      }),

      // 4. Spike detection condition
      n("4", "condition", "Error Rate Spike Detected?", 760, {
        conditionDetails: {
          conditions: [{ variable: "is_spike", operator: "eq", value: "yes" }],
        },
      }),

      // 5. AI root cause hypothesis (True path)
      aiNode(
        "5",
        "AI Hypothesize Root Cause",
        1000,
        "You are a senior SRE analyst. Given the error rate metrics, hypothesize the most likely root causes. Consider deployment changes, infrastructure issues, dependency failures, traffic spikes, and database problems. Respond JSON: {primary_hypothesis: string, secondary_hypothesis: string, recommended_investigation: string, likely_service: string, estimated_impact: string, suggested_mitigation: string}",
        "Error rate: {current_error_rate}% (baseline: {baseline_rate}%). Max rate: {max_error_rate}%. Severity: {severity_level}. Deviation: {deviation_percent}% above baseline. Metric: {metric_name}. Tags: {metric_tags}. Datapoints: {datapoint_count}.",
        [
          {
            field: "primary_hypothesis",
            as: "primary_hypothesis",
            type: "string",
          },
          {
            field: "secondary_hypothesis",
            as: "secondary_hypothesis",
            type: "string",
          },
          {
            field: "recommended_investigation",
            as: "recommended_investigation",
            type: "string",
          },
          { field: "likely_service", as: "likely_service", type: "string" },
          { field: "estimated_impact", as: "estimated_impact", type: "string" },
          {
            field: "suggested_mitigation",
            as: "suggested_mitigation",
            type: "string",
          },
        ],
      ),

      // 6. On-call engineer alert Slack
      slackNode(
        "6",
        "Alert On-Call Engineer via Slack",
        1240,
        "ERROR RATE SPIKE ALERT ({severity_level}):\\n*Current Rate:* {current_error_rate}% (baseline: {baseline_rate}%)\\n*Max Rate:* {max_error_rate}%\\n*Deviation:* +{deviation_percent}%\\n*Likely Service:* {likely_service}\\n*Primary Cause:* {primary_hypothesis}\\n*Secondary:* {secondary_hypothesis}\\n*Impact:* {estimated_impact}\\n*Investigate:* {recommended_investigation}\\n*Mitigation:* {suggested_mitigation}",
      ),

      // 7. Incident tracking Sheets
      sheetsNode(
        "7",
        "Log Error Spike to Incident Tracker",
        1480,
        "ErrorIncidents",
        '[["{check_time}","{severity_level}","{current_error_rate}","{max_error_rate}","{baseline_rate}","{deviation_percent}","{likely_service}","{primary_hypothesis}","{suggested_mitigation}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1336. EVENT RSVP AND COMMUNICATION BOT (8 nodes)
  // ===================================================================

  "Event RSVP and Communication Bot": {
    summary:
      "Receives event inquiries via webhook, extracts event data with AI, looks up the event catalog via API, generates conversational engagement with AI, processes RSVPs in HubSpot, sends confirmation emails, waits 1 day, then sends pre-event reminder SMS.",
    nodes: [
      trigger("Webhook: Event inquiry received", "webhook_app", {
        source: "event_platform",
        event: "event.inquiry_received",
        howItStarts:
          "Fires when someone inquires about an event through chat, form, or website — includes their question, contact info, and event interest",
        setup:
          "Configure webhook from your event platform or chatbot. Set OpenAI API key. Provide event catalog API or Sheets endpoint. Configure HubSpot for RSVP tracking. Set email and SMS for confirmations.",
        inputFields: [],
      }),

      // 2. AI extract event data
      aiNode(
        "2",
        "AI Extract Event Inquiry Details",
        280,
        "You are an event registration assistant. Extract the person's name, email, phone, which event they are asking about, number of guests, dietary requirements, and any questions. Respond JSON: {attendee_name: string, attendee_email: string, attendee_phone: string, event_interest: string, guest_count: number, dietary_needs: string, specific_question: string, inquiry_type: string}",
        "Raw webhook payload: {webhook_payload}. Parse all event inquiry details regardless of field naming.",
        [
          { field: "attendee_name", as: "attendee_name", type: "string" },
          { field: "attendee_email", as: "attendee_email", type: "string" },
          { field: "attendee_phone", as: "attendee_phone", type: "string" },
          { field: "event_interest", as: "event_interest", type: "string" },
          { field: "guest_count", as: "guest_count", type: "number" },
          { field: "dietary_needs", as: "dietary_needs", type: "string" },
          {
            field: "specific_question",
            as: "specific_question",
            type: "string",
          },
          { field: "inquiry_type", as: "inquiry_type", type: "string" },
        ],
      ),

      // 3. Event catalog lookup API
      n("3", "hitApi", "Look Up Event in Catalog", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/EventCatalog!A2:J100",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "event_catalog", type: "string" },
            { name: "range", customName: "catalog_range", type: "string" },
          ],
        },
      }),

      // 4. AI conversational engagement
      aiNode(
        "4",
        "AI Generate Event Response",
        760,
        "You are a friendly event coordinator. Using the event catalog, provide helpful information about the event they asked about. Include date, time, venue, pricing, availability, and answer their specific question. If the event is not found, suggest similar events. Be warm and encouraging about attending. Respond JSON: {response_message: string, event_found: string, event_date: string, event_venue: string, event_price: string, spots_available: string, registration_link: string}",
        "Attendee: {attendee_name}. Event interest: {event_interest}. Question: {specific_question}. Guests: {guest_count}. Dietary: {dietary_needs}. Event catalog data: {event_catalog}.",
        [
          { field: "response_message", as: "response_message", type: "string" },
          { field: "event_found", as: "event_found", type: "string" },
          { field: "event_date", as: "event_date", type: "string" },
          { field: "event_venue", as: "event_venue", type: "string" },
          { field: "event_price", as: "event_price", type: "string" },
          { field: "spots_available", as: "spots_available", type: "string" },
          {
            field: "registration_link",
            as: "registration_link",
            type: "string",
          },
        ],
      ),

      // 5. RSVP processing HubSpot
      hubspotPostNode(
        "5",
        "Create RSVP Contact in HubSpot",
        1000,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"firstname":"{attendee_name}","email":"{attendee_email}","phone":"{attendee_phone}","event_interest":"{event_interest}","guest_count":"{guest_count}","dietary_needs":"{dietary_needs}","event_date":"{event_date}","rsvp_status":"confirmed"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "contact_email",
            type: "string",
          },
        ],
      ),

      // 6. Confirmation email
      emailNode(
        "6",
        "Send RSVP Confirmation Email",
        1240,
        "{attendee_email}",
        "You're registered! {event_interest} — {event_date}",
        "<h2>Event Registration Confirmed</h2><p>Hi {attendee_name},</p><p>{response_message}</p><hr><h3>Event Details</h3><ul><li><strong>Event:</strong> {event_interest}</li><li><strong>Date:</strong> {event_date}</li><li><strong>Venue:</strong> {event_venue}</li><li><strong>Price:</strong> {event_price}</li><li><strong>Guests:</strong> {guest_count}</li><li><strong>Dietary Notes:</strong> {dietary_needs}</li></ul><p>We look forward to seeing you there!</p>",
      ),

      // 7. Delay 1 day
      n("7", "delayAction", "Wait 1 Day Before Reminder", 1480, {
        delayDetails: { duration: 1, unit: "days" },
      }),

      // 8. Pre-event reminder SMS
      smsNode(
        "8",
        "Send Pre-Event Reminder SMS",
        1720,
        "{attendee_phone}",
        "Hi {attendee_name}! Reminder: {event_interest} on {event_date} at {event_venue}. Guests: {guest_count}. See you there!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1337. EVENT VENUE INQUIRY BOT (7 nodes)
  // ===================================================================

  "Event Venue Inquiry Bot": {
    summary:
      "Receives venue inquiries via webhook, uses AI to assess needs, checks availability via API, matches packages with runCode, generates a proposal with AI, delivers the proposal by email, and logs the inquiry to HubSpot CRM.",
    nodes: [
      trigger("Webhook: Venue inquiry submitted", "webhook_app", {
        source: "venue_website",
        event: "venue.inquiry_submitted",
        howItStarts:
          "Fires when a potential client submits a venue inquiry through the website or chat, including event type, expected headcount, preferred dates, and budget range",
        setup:
          "Configure webhook from your venue website or inquiry form. Set OpenAI API key. Provide venue availability API or calendar integration. Configure email for proposal delivery. Set HubSpot credentials for CRM logging.",
        inputFields: [],
      }),

      // 2. AI needs assessment
      aiNode(
        "2",
        "AI Assess Venue Requirements",
        280,
        "You are a venue booking specialist. Extract and assess the client's venue requirements from the inquiry: event type, headcount, preferred dates, budget, special requirements (AV, catering, parking), and contact details. Respond JSON: {client_name: string, client_email: string, client_phone: string, event_type: string, headcount: number, preferred_dates: string, budget_range: string, av_requirements: string, catering_needed: string, parking_needed: string, special_requests: string, event_description: string}",
        "Raw webhook payload: {webhook_payload}. Parse all venue inquiry details regardless of format.",
        [
          { field: "client_name", as: "client_name", type: "string" },
          { field: "client_email", as: "client_email", type: "string" },
          { field: "client_phone", as: "client_phone", type: "string" },
          { field: "event_type", as: "event_type", type: "string" },
          { field: "headcount", as: "headcount", type: "number" },
          { field: "preferred_dates", as: "preferred_dates", type: "string" },
          { field: "budget_range", as: "budget_range", type: "string" },
          { field: "av_requirements", as: "av_requirements", type: "string" },
          { field: "catering_needed", as: "catering_needed", type: "string" },
          { field: "parking_needed", as: "parking_needed", type: "string" },
          { field: "special_requests", as: "special_requests", type: "string" },
          {
            field: "event_description",
            as: "event_description",
            type: "string",
          },
        ],
      ),

      // 3. Availability check API
      n("3", "hitApi", "Check Venue Availability", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/VenueAvailability!A2:F200",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "availability_data", type: "string" },
            { name: "range", customName: "availability_range", type: "string" },
          ],
        },
      }),

      // 4. Package matching runCode
      n("4", "runCode", "Match Venue Packages to Requirements", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Match available venue packages to client requirements
const availability = JSON.parse(vars.availability_data || "[]");
const headcount = parseInt(vars.headcount || "50", 10);
const preferredDates = vars.preferred_dates || "";
const needsCatering = (vars.catering_needed || "").toLowerCase() === "yes";
const needsAV = (vars.av_requirements || "none").toLowerCase() !== "none";
const needsParking = (vars.parking_needed || "").toLowerCase() === "yes";
let matchedSpaces = [];
let bestMatch = null;
let bestScore = 0;
for (const row of availability) {
  const spaceName = row[0] || "";
  const capacity = parseInt(row[1] || "0", 10);
  const hasAV = (row[2] || "").toLowerCase() === "yes";
  const hasCatering = (row[3] || "").toLowerCase() === "yes";
  const basePrice = parseInt(row[4] || "0", 10);
  const available = (row[5] || "").toLowerCase() !== "booked";
  if (!available || capacity < headcount) continue;
  let score = 0;
  if (capacity >= headcount && capacity <= headcount * 1.5) score += 30;
  if (needsAV && hasAV) score += 20;
  if (needsCatering && hasCatering) score += 20;
  score += 10; // available bonus
  matchedSpaces.push(spaceName + " (cap:" + capacity + ", $" + basePrice + ")");
  if (score > bestScore) { bestScore = score; bestMatch = spaceName + " — $" + basePrice; }
}
return {
  matched_spaces: matchedSpaces.join(" | ") || "No exact matches — custom arrangement needed",
  matched_count: matchedSpaces.length,
  best_match: bestMatch || "Custom arrangement recommended",
  match_score: bestScore,
  has_availability: matchedSpaces.length > 0 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "matched_spaces", type: "string" },
            { name: "matched_count", type: "number" },
            { name: "best_match", type: "string" },
            { name: "match_score", type: "number" },
            { name: "has_availability", type: "string" },
          ],
        },
      }),

      // 5. AI proposal generation
      aiNode(
        "5",
        "AI Generate Venue Proposal",
        1000,
        "You are a venue sales specialist. Create a professional, personalized venue proposal for the client based on their requirements and matched venues. Include pricing estimates, amenities, setup options, and next steps. Be warm and professional. Respond JSON: {proposal_text: string, estimated_total: string, recommended_package: string, next_steps: string, value_propositions: string}",
        "Client: {client_name}. Event: {event_type} for {headcount} guests. Dates: {preferred_dates}. Budget: {budget_range}. AV: {av_requirements}. Catering: {catering_needed}. Parking: {parking_needed}. Special: {special_requests}. Matched venues: {matched_spaces}. Best match: {best_match}. Description: {event_description}.",
        [
          { field: "proposal_text", as: "proposal_text", type: "string" },
          { field: "estimated_total", as: "estimated_total", type: "string" },
          {
            field: "recommended_package",
            as: "recommended_package",
            type: "string",
          },
          { field: "next_steps", as: "next_steps", type: "string" },
          {
            field: "value_propositions",
            as: "value_propositions",
            type: "string",
          },
        ],
      ),

      // 6. Proposal delivery email
      emailNode(
        "6",
        "Send Venue Proposal Email",
        1240,
        "{client_email}",
        "Your Custom Venue Proposal — {event_type} for {headcount} Guests",
        "<h2>Venue Proposal for {client_name}</h2><p>{proposal_text}</p><hr><h3>Recommended Package</h3><p>{recommended_package}</p><h3>Estimated Total</h3><p>{estimated_total}</p><h3>Why Choose Us</h3><p>{value_propositions}</p><h3>Next Steps</h3><p>{next_steps}</p><p>We would love to host your {event_type}! Reply to this email or call us to schedule a tour.</p>",
      ),

      // 7. CRM logging HubSpot
      hubspotPostNode(
        "7",
        "Log Venue Inquiry to HubSpot",
        1480,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"firstname":"{client_name}","email":"{client_email}","phone":"{client_phone}","event_type":"{event_type}","headcount":"{headcount}","budget_range":"{budget_range}","preferred_dates":"{preferred_dates}","venue_match":"{best_match}","proposal_sent":"yes","lifecyclestage":"lead"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1338. FEATURE REQUEST COLLECTOR (8 nodes)
  // ===================================================================

  "Feature Request Collector": {
    summary:
      "Receives feature requests via webhook, parses them with AI, checks for duplicates with runCode, scores business impact with runCode, categorizes the request with runCode, notifies the product team via Slack, sends acknowledgment to the requester, and logs to a request database in Sheets.",
    nodes: [
      trigger("Webhook: Feature request submitted", "webhook_app", {
        source: "feedback_system",
        event: "feature.request_submitted",
        howItStarts:
          "Fires when a user, customer, or team member submits a feature request through a feedback form, in-app widget, or support ticket",
        setup:
          "Configure webhook from your feedback collection system. Set OpenAI API key. Provide Google Sheets spreadsheet ID for the feature request database. Configure Slack for product team notifications. Set up email for requester acknowledgments.",
        inputFields: [],
      }),

      // 2. AI request parsing
      aiNode(
        "2",
        "AI Parse Feature Request",
        280,
        "You are a product management assistant. Parse the feature request to extract: the requester info, feature title, detailed description, use case, business justification, priority suggestion, and affected product area. Respond JSON: {requester_name: string, requester_email: string, requester_role: string, feature_title: string, feature_description: string, use_case: string, business_justification: string, suggested_priority: string, product_area: string, customer_segment: string}",
        "Raw webhook payload: {webhook_payload}. Extract all feature request details regardless of field naming.",
        [
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "requester_role", as: "requester_role", type: "string" },
          { field: "feature_title", as: "feature_title", type: "string" },
          {
            field: "feature_description",
            as: "feature_description",
            type: "string",
          },
          { field: "use_case", as: "use_case", type: "string" },
          {
            field: "business_justification",
            as: "business_justification",
            type: "string",
          },
          {
            field: "suggested_priority",
            as: "suggested_priority",
            type: "string",
          },
          { field: "product_area", as: "product_area", type: "string" },
          { field: "customer_segment", as: "customer_segment", type: "string" },
        ],
      ),

      // 3. Duplicate detection runCode
      n("3", "runCode", "Check for Duplicate Feature Requests", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Simple duplicate detection based on title and description keywords
const title = (vars.feature_title || "").toLowerCase();
const description = (vars.feature_description || "").toLowerCase();
const keywords = title.split(/\\s+/).filter(w => w.length > 3);
const titleHash = keywords.sort().join("-");
// In production, this would query existing requests from the database
// For now, generate a unique request ID and similarity indicator
const requestId = "FR-" + Date.now().toString(36).toUpperCase();
const isDuplicate = "no"; // Would be determined by database query
const similarityNote = "New request — no exact duplicates detected based on keyword analysis";
return {
  request_id: requestId,
  is_duplicate: isDuplicate,
  title_hash: titleHash,
  similarity_note: similarityNote,
  keyword_count: keywords.length,
  submitted_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "request_id", type: "string" },
            { name: "is_duplicate", type: "string" },
            { name: "title_hash", type: "string" },
            { name: "similarity_note", type: "string" },
            { name: "keyword_count", type: "number" },
            { name: "submitted_at", type: "string" },
          ],
        },
      }),

      // 4. Impact scoring runCode
      n("4", "runCode", "Calculate Business Impact Score", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score business impact based on multiple factors
const priority = (vars.suggested_priority || "medium").toLowerCase();
const segment = (vars.customer_segment || "").toLowerCase();
const hasJustification = vars.business_justification && vars.business_justification.length > 20;
const role = (vars.requester_role || "").toLowerCase();
let score = 0;
// Priority weighting
if (priority === "critical") score += 30;
else if (priority === "high") score += 20;
else if (priority === "medium") score += 10;
else score += 5;
// Segment weighting
if (segment.includes("enterprise")) score += 25;
else if (segment.includes("business") || segment.includes("pro")) score += 15;
else score += 5;
// Justification quality
if (hasJustification) score += 15;
// Role weighting
if (role.includes("exec") || role.includes("vp") || role.includes("director")) score += 20;
else if (role.includes("manager")) score += 10;
else score += 5;
const impactLevel = score >= 70 ? "HIGH" : score >= 40 ? "MEDIUM" : "LOW";
return {
  impact_score: Math.min(100, score),
  impact_level: impactLevel,
  scoring_breakdown: "Priority: " + priority + " | Segment: " + segment + " | Role: " + role + " | Justification: " + (hasJustification ? "provided" : "missing")
};`,
          outputVariables: [
            { name: "impact_score", type: "number" },
            { name: "impact_level", type: "string" },
            { name: "scoring_breakdown", type: "string" },
          ],
        },
      }),

      // 5. Categorization runCode
      n("5", "runCode", "Categorize Feature Request", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Categorize feature request by type and effort
const description = (vars.feature_description || "").toLowerCase();
const area = (vars.product_area || "general").toLowerCase();
let category = "enhancement";
if (description.includes("bug") || description.includes("fix") || description.includes("broken")) category = "bug_fix";
else if (description.includes("new") || description.includes("add") || description.includes("create")) category = "new_feature";
else if (description.includes("improve") || description.includes("better") || description.includes("faster")) category = "enhancement";
else if (description.includes("integrate") || description.includes("connect") || description.includes("api")) category = "integration";
else if (description.includes("remove") || description.includes("deprecate")) category = "deprecation";
const wordCount = (vars.feature_description || "").split(/\\s+/).length;
const estimatedEffort = wordCount > 100 ? "large" : wordCount > 40 ? "medium" : "small";
return {
  category: category,
  estimated_effort: estimatedEffort,
  product_area_normalized: area,
  classification_label: category.toUpperCase() + " — " + estimatedEffort + " effort — " + area
};`,
          outputVariables: [
            { name: "category", type: "string" },
            { name: "estimated_effort", type: "string" },
            { name: "product_area_normalized", type: "string" },
            { name: "classification_label", type: "string" },
          ],
        },
      }),

      // 6. Product team notification Slack
      slackNode(
        "6",
        "Notify Product Team via Slack",
        1240,
        "New Feature Request ({request_id}):\\n*Title:* {feature_title}\\n*From:* {requester_name} ({requester_role}) — {customer_segment}\\n*Area:* {product_area}\\n*Category:* {classification_label}\\n*Impact:* {impact_level} ({impact_score}/100)\\n*Description:* {feature_description}\\n*Use Case:* {use_case}\\n*Business Justification:* {business_justification}",
      ),

      // 7. Requester acknowledgment email
      emailNode(
        "7",
        "Send Acknowledgment to Requester",
        1480,
        "{requester_email}",
        "Feature Request Received — {request_id}: {feature_title}",
        "<h2>Thank You for Your Feature Request</h2><p>Hi {requester_name},</p><p>We have received your feature request and assigned it tracking ID <strong>{request_id}</strong>.</p><h3>Request Summary</h3><ul><li><strong>Title:</strong> {feature_title}</li><li><strong>Area:</strong> {product_area}</li><li><strong>Category:</strong> {category}</li><li><strong>Impact Assessment:</strong> {impact_level}</li></ul><p>Our product team is reviewing your request. We will keep you updated on its progress.</p>",
        400,
      ),

      // 8. Request database Sheets
      sheetsNode(
        "8",
        "Log to Feature Request Database",
        1720,
        "FeatureRequests",
        '[["{submitted_at}","{request_id}","{feature_title}","{requester_name}","{requester_email}","{product_area}","{category}","{impact_score}","{impact_level}","{estimated_effort}","{customer_segment}","{is_duplicate}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1339. FILE ORGANIZER AUTOMATION (7 nodes)
  // ===================================================================

  "File Organizer Automation": {
    summary:
      "Triggers on file uploads via webhook, extracts file metadata with runCode, classifies the file type with AI, standardizes naming with runCode, routes to the correct folder via API, assigns permissions with runCode, and logs the organization action to Sheets.",
    nodes: [
      trigger("Webhook: File uploaded", "webhook_app", {
        source: "file_storage",
        event: "file.uploaded",
        howItStarts:
          "Fires when a new file is uploaded to the monitored storage location, including file name, type, size, and uploader information",
        setup:
          "Configure webhook from your file storage provider (Google Drive, Dropbox, S3). Set OpenAI API key for classification. Provide folder structure mapping. Configure Google Sheets for organization logging.",
        inputFields: [],
      }),

      // 2. File metadata extraction runCode
      n("2", "runCode", "Extract File Metadata", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse file upload webhook and extract metadata
const payload = JSON.parse(vars.webhook_payload || "{}");
const fileName = payload.file_name || payload.name || payload.filename || "unknown_file";
const fileExt = fileName.includes(".") ? fileName.split(".").pop().toLowerCase() : "unknown";
const fileSize = parseInt(payload.size || payload.file_size || "0", 10);
const mimeType = payload.mime_type || payload.content_type || payload.type || "";
const uploadedBy = payload.uploaded_by || payload.owner || payload.user_email || "";
const uploadPath = payload.path || payload.parent_folder || payload.location || "/uploads";
const fileId = payload.file_id || payload.id || payload.resource_id || "";
const fileSizeKB = Math.round(fileSize / 1024);
const fileSizeMB = (fileSize / (1024 * 1024)).toFixed(2);
const isLargeFile = fileSize > 10 * 1024 * 1024 ? "yes" : "no";
return {
  file_id: fileId,
  file_name: fileName,
  file_extension: fileExt,
  file_size_bytes: fileSize,
  file_size_display: fileSizeMB + " MB",
  mime_type: mimeType,
  uploaded_by: uploadedBy,
  upload_path: uploadPath,
  is_large_file: isLargeFile,
  upload_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "file_id", type: "string" },
            { name: "file_name", type: "string" },
            { name: "file_extension", type: "string" },
            { name: "file_size_bytes", type: "number" },
            { name: "file_size_display", type: "string" },
            { name: "mime_type", type: "string" },
            { name: "uploaded_by", type: "string" },
            { name: "upload_path", type: "string" },
            { name: "is_large_file", type: "string" },
            { name: "upload_timestamp", type: "string" },
          ],
        },
      }),

      // 3. AI classification
      aiNode(
        "3",
        "AI Classify File Type and Category",
        520,
        "You are a file organization specialist. Based on the file name, extension, MIME type, and upload context, classify the file into a category and determine the appropriate folder. Categories: contracts, invoices, reports, presentations, images, designs, code, data, hr_documents, marketing, legal, general. Respond JSON: {file_category: string, target_folder: string, department: string, is_sensitive: string, retention_period: string, suggested_name: string}",
        "File: {file_name} (ext: {file_extension}, MIME: {mime_type}). Size: {file_size_display}. Uploaded by: {uploaded_by}. Path: {upload_path}. Classify and recommend folder placement.",
        [
          { field: "file_category", as: "file_category", type: "string" },
          { field: "target_folder", as: "target_folder", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "is_sensitive", as: "is_sensitive", type: "string" },
          { field: "retention_period", as: "retention_period", type: "string" },
          { field: "suggested_name", as: "suggested_name", type: "string" },
        ],
      ),

      // 4. Naming standardization runCode
      n("4", "runCode", "Standardize File Name", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Standardize file naming convention: YYYY-MM-DD_Category_Name.ext
const now = new Date();
const datePrefix = now.toISOString().split("T")[0];
const category = (vars.file_category || "general").toLowerCase().replace(/\\s+/g, "_");
const suggestedName = vars.suggested_name || vars.file_name;
const cleanName = suggestedName
  .replace(/[^a-zA-Z0-9._-]/g, "_")
  .replace(/_+/g, "_")
  .replace(/^_|_$/g, "");
const ext = vars.file_extension || "dat";
const standardizedName = datePrefix + "_" + category + "_" + cleanName + (cleanName.endsWith("." + ext) ? "" : "." + ext);
const fullTargetPath = (vars.target_folder || "/organized") + "/" + standardizedName;
return {
  standardized_name: standardizedName,
  full_target_path: fullTargetPath,
  original_name: vars.file_name,
  naming_applied: "yes"
};`,
          outputVariables: [
            { name: "standardized_name", type: "string" },
            { name: "full_target_path", type: "string" },
            { name: "original_name", type: "string" },
            { name: "naming_applied", type: "string" },
          ],
        },
      }),

      // 5. Folder routing API (Google Drive move)
      n("5", "hitApi", "Move File to Target Folder", 1000, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://www.googleapis.com/drive/v3/files/{file_id}?addParents={target_folder_id}&removeParents={source_folder_id}",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "name", value: "{standardized_name}" }],
          variables: [
            { name: "id", customName: "moved_file_id", type: "string" },
            { name: "name", customName: "final_file_name", type: "string" },
            {
              name: "parents[0]",
              customName: "final_parent_folder",
              type: "string",
            },
          ],
        },
      }),

      // 6. Permission assignment runCode
      n("6", "runCode", "Determine File Permissions", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine appropriate permissions based on category and sensitivity
const category = (vars.file_category || "general").toLowerCase();
const isSensitive = (vars.is_sensitive || "no").toLowerCase() === "yes";
const department = (vars.department || "general").toLowerCase();
let accessLevel = "department";
let shareWith = department + "_team";
if (isSensitive) {
  accessLevel = "restricted";
  shareWith = department + "_managers";
}
if (category === "contracts" || category === "legal") {
  accessLevel = "restricted";
  shareWith = "legal_team";
}
if (category === "hr_documents") {
  accessLevel = "confidential";
  shareWith = "hr_team";
}
return {
  access_level: accessLevel,
  share_with: shareWith,
  permission_notes: "Access: " + accessLevel + " | Shared with: " + shareWith + " | Sensitive: " + (isSensitive ? "YES" : "NO"),
  retention: vars.retention_period || "standard"
};`,
          outputVariables: [
            { name: "access_level", type: "string" },
            { name: "share_with", type: "string" },
            { name: "permission_notes", type: "string" },
            { name: "retention", type: "string" },
          ],
        },
      }),

      // 7. Organization log Sheets
      sheetsNode(
        "7",
        "Log File Organization to Sheets",
        1480,
        "FileOrganization",
        '[["{upload_timestamp}","{file_id}","{original_name}","{standardized_name}","{file_category}","{target_folder}","{department}","{access_level}","{file_size_display}","{uploaded_by}","{is_sensitive}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1340. GITHUB TO SLACK PR ALERTS (6 nodes)
  // ===================================================================

  "GitHub to Slack PR Alerts": {
    summary:
      "Receives GitHub PR events via webhook, extracts PR data with AI, generates a change summary with AI, formats the notification with runCode, posts to the appropriate Slack channel, and checks if review reminders are needed via condition.",
    nodes: [
      trigger("Webhook: GitHub PR event", "webhook_app", {
        source: "github",
        event: "pull_request",
        howItStarts:
          "Fires on GitHub pull request events (opened, updated, merged, closed), including PR details, author, branch info, and changed files",
        setup:
          "Configure GitHub webhook with 'Pull requests' event selected. Set your repository webhook secret. Set OpenAI API key for change summarization. Configure Slack webhook URL for the engineering channel.",
        inputFields: [],
      }),

      // 2. AI extract PR data
      aiNode(
        "2",
        "AI Extract PR Details",
        280,
        "You are a GitHub webhook parser. Extract pull request details from the webhook payload: PR number, title, author, action (opened/closed/merged), base branch, head branch, description, number of files changed, additions, deletions, and whether it needs review. Respond JSON: {pr_number: number, pr_title: string, pr_author: string, pr_action: string, base_branch: string, head_branch: string, pr_description: string, files_changed: number, additions: number, deletions: number, pr_url: string, needs_review: string, is_draft: string}",
        "Raw GitHub webhook payload: {webhook_payload}. Parse all PR details.",
        [
          { field: "pr_number", as: "pr_number", type: "number" },
          { field: "pr_title", as: "pr_title", type: "string" },
          { field: "pr_author", as: "pr_author", type: "string" },
          { field: "pr_action", as: "pr_action", type: "string" },
          { field: "base_branch", as: "base_branch", type: "string" },
          { field: "head_branch", as: "head_branch", type: "string" },
          { field: "pr_description", as: "pr_description", type: "string" },
          { field: "files_changed", as: "files_changed", type: "number" },
          { field: "additions", as: "additions", type: "number" },
          { field: "deletions", as: "deletions", type: "number" },
          { field: "pr_url", as: "pr_url", type: "string" },
          { field: "needs_review", as: "needs_review", type: "string" },
          { field: "is_draft", as: "is_draft", type: "string" },
        ],
      ),

      // 3. AI change summary
      aiNode(
        "3",
        "AI Summarize PR Changes",
        520,
        "You are a code review assistant. Summarize the pull request changes in 2-3 sentences for the engineering team. Focus on what the PR does, potential impact, and areas that need careful review. Respond JSON: {change_summary: string, risk_areas: string, review_priority: string, estimated_review_time: string}",
        "PR #{pr_number}: {pr_title} by {pr_author}. {head_branch} -> {base_branch}. Files: {files_changed}, +{additions}/-{deletions}. Description: {pr_description}. Action: {pr_action}.",
        [
          { field: "change_summary", as: "change_summary", type: "string" },
          { field: "risk_areas", as: "risk_areas", type: "string" },
          { field: "review_priority", as: "review_priority", type: "string" },
          {
            field: "estimated_review_time",
            as: "estimated_review_time",
            type: "string",
          },
        ],
      ),

      // 4. Notification formatting runCode
      n("4", "runCode", "Format Slack PR Notification", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format a rich Slack notification for the PR event
const action = vars.pr_action || "updated";
const emoji = action === "opened" ? "new" : action === "merged" ? "white_check_mark" : action === "closed" ? "x" : "arrows_counterclockwise";
const actionLabel = action.charAt(0).toUpperCase() + action.slice(1);
const sizeLabel = parseInt(vars.files_changed || "0", 10) > 20 ? "LARGE" : parseInt(vars.files_changed || "0", 10) > 5 ? "medium" : "small";
const isDraft = vars.is_draft === "yes" ? " [DRAFT]" : "";
const slackMessage = ":" + emoji + ": *PR " + actionLabel + isDraft + "* — #" + vars.pr_number + " " + vars.pr_title +
  "\\nAuthor: " + vars.pr_author + " | " + vars.head_branch + " → " + vars.base_branch +
  "\\nSize: " + sizeLabel + " (" + vars.files_changed + " files, +" + vars.additions + "/-" + vars.deletions + ")" +
  "\\nSummary: " + vars.change_summary +
  "\\nRisk Areas: " + vars.risk_areas +
  "\\nReview Priority: " + vars.review_priority + " (~" + vars.estimated_review_time + ")" +
  "\\nLink: " + vars.pr_url;
return {
  formatted_message: slackMessage,
  pr_size: sizeLabel,
  action_label: actionLabel
};`,
          outputVariables: [
            { name: "formatted_message", type: "string" },
            { name: "pr_size", type: "string" },
            { name: "action_label", type: "string" },
          ],
        },
      }),

      // 5. Channel routing Slack
      slackNode("5", "Post PR Alert to Slack", 1000, "{formatted_message}"),

      // 6. Review reminder condition
      n("6", "condition", "PR Needs Review and Not Draft?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "needs_review", operator: "eq", value: "yes" },
          ],
        },
      }),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 1341. GOOGLE SHEETS TO CRM SYNC (7 nodes)
  // ===================================================================

  "Google Sheets to CRM Sync": {
    summary:
      "Triggers on sheet changes via webhook, extracts row data with runCode, checks for CRM duplicates in HubSpot, maps fields with runCode, executes the CRM operation in HubSpot, updates the sheet status via API, and logs errors to a separate Sheets tab.",
    nodes: [
      trigger("Webhook: Google Sheet row changed", "webhook_app", {
        source: "google_sheets",
        event: "sheet.row_updated",
        howItStarts:
          "Fires when a row is added or modified in the monitored Google Sheet, typically from a lead capture form, data import, or manual entry",
        setup:
          "Configure Google Sheets webhook (via Apps Script or Zapier trigger). Set HubSpot API credentials. Provide field mapping configuration. Set spreadsheet ID for status updates and error logging.",
        inputFields: [],
      }),

      // 2. Row data extraction runCode
      n("2", "runCode", "Extract Row Data from Sheet Change", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse the sheet change webhook payload
const payload = JSON.parse(vars.webhook_payload || "{}");
const rowData = payload.row || payload.values || payload.data || {};
const rowNumber = payload.row_number || payload.range || "unknown";
const sheetName = payload.sheet_name || payload.tab || "Sheet1";
// Extract standard CRM fields from row data (handle both array and object)
let firstName, lastName, email, phone, company, title, source, notes;
if (Array.isArray(rowData)) {
  firstName = rowData[0] || "";
  lastName = rowData[1] || "";
  email = rowData[2] || "";
  phone = rowData[3] || "";
  company = rowData[4] || "";
  title = rowData[5] || "";
  source = rowData[6] || "";
  notes = rowData[7] || "";
} else {
  firstName = rowData.first_name || rowData.firstName || rowData.name || "";
  lastName = rowData.last_name || rowData.lastName || "";
  email = rowData.email || rowData.Email || "";
  phone = rowData.phone || rowData.Phone || "";
  company = rowData.company || rowData.Company || "";
  title = rowData.title || rowData.job_title || "";
  source = rowData.source || rowData.lead_source || "";
  notes = rowData.notes || rowData.Notes || "";
}
const isValid = email && email.includes("@");
return {
  row_number: String(rowNumber),
  sheet_name: sheetName,
  first_name: firstName,
  last_name: lastName,
  contact_email: email,
  contact_phone: phone,
  company_name: company,
  job_title: title,
  lead_source: source,
  contact_notes: notes,
  is_valid_row: isValid ? "yes" : "no",
  extracted_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "row_number", type: "string" },
            { name: "sheet_name", type: "string" },
            { name: "first_name", type: "string" },
            { name: "last_name", type: "string" },
            { name: "contact_email", type: "string" },
            { name: "contact_phone", type: "string" },
            { name: "company_name", type: "string" },
            { name: "job_title", type: "string" },
            { name: "lead_source", type: "string" },
            { name: "contact_notes", type: "string" },
            { name: "is_valid_row", type: "string" },
            { name: "extracted_at", type: "string" },
          ],
        },
      }),

      // 3. CRM duplicate check HubSpot
      hubspotGetNode(
        "3",
        "Check for Duplicate in HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          { name: "total", customName: "duplicate_count", type: "number" },
          {
            name: "results[0].id",
            customName: "existing_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.email",
            customName: "existing_email",
            type: "string",
          },
        ],
      ),

      // 4. Field mapping runCode
      n("4", "runCode", "Map Sheet Fields to CRM Properties", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map extracted sheet data to HubSpot CRM properties
const isDuplicate = parseInt(vars.duplicate_count || "0", 10) > 0;
const existingId = vars.existing_contact_id || "";
const operation = isDuplicate ? "update" : "create";
const properties = {
  firstname: vars.first_name || "",
  lastname: vars.last_name || "",
  email: vars.contact_email || "",
  phone: vars.contact_phone || "",
  company: vars.company_name || "",
  jobtitle: vars.job_title || "",
  hs_lead_status: "NEW",
  lead_source: vars.lead_source || "Google Sheet Import",
  notes_last_updated: vars.contact_notes || ""
};
return {
  crm_operation: operation,
  existing_id: existingId,
  is_duplicate: isDuplicate ? "yes" : "no",
  mapped_properties: JSON.stringify(properties),
  property_count: Object.keys(properties).filter(k => properties[k]).length
};`,
          outputVariables: [
            { name: "crm_operation", type: "string" },
            { name: "existing_id", type: "string" },
            { name: "is_duplicate", type: "string" },
            { name: "mapped_properties", type: "string" },
            { name: "property_count", type: "number" },
          ],
        },
      }),

      // 5. CRM operation execution HubSpot
      hubspotPostNode(
        "5",
        "Create or Update HubSpot Contact",
        1000,
        "/crm/v3/objects/contacts",
        [{ key: "properties", value: "{mapped_properties}" }],
        [
          { name: "id", customName: "synced_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "synced_email",
            type: "string",
          },
          { name: "createdAt", customName: "crm_created_at", type: "string" },
        ],
      ),

      // 6. Sheet status update API
      n("6", "hitApi", "Update Sheet Row Status", 1240, {
        hitApiDetails: {
          method: "PUT",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/{sheet_name}!I{row_number}?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value: '[["Synced to CRM — {synced_contact_id}"]]',
            },
          ],
          variables: [
            {
              name: "updatedCells",
              customName: "cells_updated",
              type: "number",
            },
          ],
        },
      }),

      // 7. Error handling Sheets
      sheetsNode(
        "7",
        "Log Sync Operation to Error Tracker",
        1480,
        "SyncLog",
        '[["{extracted_at}","{contact_email}","{crm_operation}","{is_duplicate}","{synced_contact_id}","{row_number}","{sheet_name}","{property_count}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1342. GRANT APPLICATION TRACKER (8 nodes)
  // ===================================================================

  "Grant Application Tracker": {
    summary:
      "Runs on a schedule to discover grant deadlines via API, assesses opportunities with runCode, updates the application pipeline in Sheets, checks deadline urgency with a condition, tracks document assembly with runCode, uses AI for application review, and sends submission confirmations via email.",
    nodes: [
      trigger("Schedule: Weekly grant deadline scan", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts:
          "Runs weekly to scan for upcoming grant deadlines, new funding opportunities, and application status updates across all tracked grant programs",
        setup:
          "Configure grant database API access (Grants.gov, Foundation Center, or custom). Set Google Sheets for pipeline tracking. Set OpenAI API key for application review. Configure email for team notifications.",
        inputFields: [],
      }),

      // 2. Grant deadline discovery API
      n("2", "hitApi", "Fetch Grant Opportunities from Database", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.grants.gov/v2/opportunities?postedDateRange=lastWeek&status=posted&limit=50",
          headers: [
            { key: "Authorization", value: "Bearer {grants_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "opportunities",
              customName: "grant_opportunities",
              type: "string",
            },
            {
              name: "totalCount",
              customName: "total_opportunities",
              type: "number",
            },
          ],
        },
      }),

      // 3. Opportunity assessment runCode
      n("3", "runCode", "Assess Grant Opportunities", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze grant opportunities for relevance and deadline urgency
const opportunities = JSON.parse(vars.grant_opportunities || "[]");
const now = new Date();
let relevantGrants = [];
let urgentGrants = [];
let totalAmount = 0;
for (const grant of opportunities) {
  const title = grant.title || grant.opportunity_title || "Untitled";
  const deadline = grant.close_date || grant.deadline || "";
  const amount = parseInt(grant.award_ceiling || grant.max_amount || "0", 10);
  const agency = grant.agency_name || grant.funder || "Unknown";
  const category = grant.category || grant.funding_instrument || "grant";
  if (!deadline) continue;
  const daysUntil = Math.floor((new Date(deadline) - now) / (1000 * 60 * 60 * 24));
  const entry = title + " | " + agency + " | $" + amount.toLocaleString() + " | " + daysUntil + " days";
  if (daysUntil > 0 && daysUntil <= 90) relevantGrants.push(entry);
  if (daysUntil > 0 && daysUntil <= 14) urgentGrants.push(entry);
  totalAmount += amount;
}
const hasUrgent = urgentGrants.length > 0 ? "yes" : "no";
return {
  relevant_grants: relevantGrants.join(" || ") || "No relevant grants found this scan",
  relevant_count: relevantGrants.length,
  urgent_grants: urgentGrants.join(" || ") || "No urgent deadlines",
  urgent_count: urgentGrants.length,
  has_urgent: hasUrgent,
  total_funding_available: "$" + totalAmount.toLocaleString(),
  scan_date: now.toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "relevant_grants", type: "string" },
            { name: "relevant_count", type: "number" },
            { name: "urgent_grants", type: "string" },
            { name: "urgent_count", type: "number" },
            { name: "has_urgent", type: "string" },
            { name: "total_funding_available", type: "string" },
            { name: "scan_date", type: "string" },
          ],
        },
      }),

      // 4. Application pipeline Sheets
      sheetsNode(
        "4",
        "Update Grant Pipeline in Sheets",
        760,
        "GrantPipeline",
        '[["{scan_date}","{relevant_count}","{urgent_count}","{total_funding_available}","{relevant_grants}"]]',
      ),

      // 5. Deadline alert condition
      n("5", "condition", "Any Urgent Grant Deadlines?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "has_urgent", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 6. Document assembly tracking runCode (True path)
      n("6", "runCode", "Check Document Readiness for Urgent Grants", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check readiness of standard grant application documents
const standardDocs = [
  { name: "Organization Profile", ready: true },
  { name: "Budget Narrative", ready: true },
  { name: "Project Timeline", ready: false },
  { name: "Letters of Support", ready: false },
  { name: "Financial Statements", ready: true },
  { name: "Board Resolution", ready: true },
  { name: "Logic Model", ready: false }
];
const readyDocs = standardDocs.filter(d => d.ready).map(d => d.name);
const missingDocs = standardDocs.filter(d => !d.ready).map(d => d.name);
const readinessScore = Math.round((readyDocs.length / standardDocs.length) * 100);
return {
  ready_documents: readyDocs.join(", "),
  missing_documents: missingDocs.join(", "),
  readiness_score: readinessScore,
  ready_count: readyDocs.length,
  missing_count: missingDocs.length,
  total_required: standardDocs.length
};`,
          outputVariables: [
            { name: "ready_documents", type: "string" },
            { name: "missing_documents", type: "string" },
            { name: "readiness_score", type: "number" },
            { name: "ready_count", type: "number" },
            { name: "missing_count", type: "number" },
            { name: "total_required", type: "number" },
          ],
        },
      }),

      // 7. AI application review
      aiNode(
        "7",
        "AI Review Application Readiness",
        1480,
        "You are a grant writing consultant. Review the application readiness status and provide specific recommendations for improving the application before the deadline. Identify the most critical missing items and suggest strategies. Respond JSON: {readiness_assessment: string, critical_gaps: string, improvement_recommendations: string, submission_likelihood: string, priority_actions: string}",
        "Urgent grants: {urgent_grants}. Document readiness: {readiness_score}%. Ready: {ready_documents}. Missing: {missing_documents}. Provide actionable recommendations for meeting the deadline.",
        [
          {
            field: "readiness_assessment",
            as: "readiness_assessment",
            type: "string",
          },
          { field: "critical_gaps", as: "critical_gaps", type: "string" },
          {
            field: "improvement_recommendations",
            as: "improvement_recommendations",
            type: "string",
          },
          {
            field: "submission_likelihood",
            as: "submission_likelihood",
            type: "string",
          },
          { field: "priority_actions", as: "priority_actions", type: "string" },
        ],
      ),

      // 8. Submission confirmation email
      emailNode(
        "8",
        "Send Grant Deadline Alert Email",
        1720,
        "{grants_team_email}",
        "Grant Deadline Alert — {urgent_count} Urgent Deadlines ({scan_date})",
        "<h2>Weekly Grant Scan Results</h2><h3>Urgent Deadlines ({urgent_count})</h3><p>{urgent_grants}</p><h3>Application Readiness: {readiness_score}%</h3><p><strong>Ready:</strong> {ready_documents}</p><p><strong>Missing:</strong> {missing_documents}</p><h3>AI Assessment</h3><p>{readiness_assessment}</p><p><strong>Critical Gaps:</strong> {critical_gaps}</p><p><strong>Priority Actions:</strong> {priority_actions}</p><p><strong>Submission Likelihood:</strong> {submission_likelihood}</p><h3>All Relevant Opportunities ({relevant_count})</h3><p>{relevant_grants}</p><p>Total funding available: {total_funding_available}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1343. GUEST FEEDBACK COLLECTION BOT (7 nodes + Slack alert on 8th)
  // ===================================================================

  "Guest Feedback Collection Bot": {
    summary:
      "Triggers post-visit via webhook, extracts guest context with AI, generates a personalized survey with AI, delivers the survey via email, waits 3 days for responses, analyzes feedback with AI, checks for service recovery needs via condition, and alerts management on Slack for negative feedback.",
    nodes: [
      trigger("Webhook: Guest checkout or visit completed", "webhook_app", {
        source: "hospitality_system",
        event: "guest.checkout_completed",
        howItStarts:
          "Fires when a guest checks out from a hotel, completes a restaurant visit, or finishes a service appointment, including the guest profile and visit details",
        setup:
          "Configure webhook from your hospitality/POS system. Set OpenAI API key for personalized survey generation and analysis. Configure email for survey delivery. Set Slack webhook for service recovery alerts. Provide Google Sheets for feedback tracking.",
        inputFields: [],
      }),

      // 2. AI extract guest context
      aiNode(
        "2",
        "AI Extract Guest and Visit Context",
        280,
        "You are a hospitality analytics assistant. Extract guest information and visit details from the checkout webhook: guest name, email, phone, visit type, duration, services used, spend amount, loyalty status, and any notes from staff. Respond JSON: {guest_name: string, guest_email: string, guest_phone: string, visit_type: string, visit_duration: string, services_used: string, spend_amount: string, loyalty_tier: string, staff_notes: string, visit_date: string}",
        "Raw webhook payload: {webhook_payload}. Extract all guest and visit details regardless of field naming.",
        [
          { field: "guest_name", as: "guest_name", type: "string" },
          { field: "guest_email", as: "guest_email", type: "string" },
          { field: "guest_phone", as: "guest_phone", type: "string" },
          { field: "visit_type", as: "visit_type", type: "string" },
          { field: "visit_duration", as: "visit_duration", type: "string" },
          { field: "services_used", as: "services_used", type: "string" },
          { field: "spend_amount", as: "spend_amount", type: "string" },
          { field: "loyalty_tier", as: "loyalty_tier", type: "string" },
          { field: "staff_notes", as: "staff_notes", type: "string" },
          { field: "visit_date", as: "visit_date", type: "string" },
        ],
      ),

      // 3. AI personalized survey generation
      aiNode(
        "3",
        "AI Generate Personalized Survey",
        520,
        "You are a customer experience specialist. Create a personalized feedback survey for the guest based on their visit. Include 3-5 questions specific to the services they used, plus a general satisfaction question and NPS question. Be warm and reference their specific experience. Respond JSON: {survey_intro: string, questions: string, nps_question: string, closing_message: string, survey_link_params: string}",
        "Guest: {guest_name} ({loyalty_tier}). Visit: {visit_type} on {visit_date} ({visit_duration}). Services: {services_used}. Spend: {spend_amount}. Staff notes: {staff_notes}. Create a personalized survey.",
        [
          { field: "survey_intro", as: "survey_intro", type: "string" },
          { field: "questions", as: "survey_questions", type: "string" },
          { field: "nps_question", as: "nps_question", type: "string" },
          { field: "closing_message", as: "closing_message", type: "string" },
          {
            field: "survey_link_params",
            as: "survey_link_params",
            type: "string",
          },
        ],
      ),

      // 4. Multi-channel survey email
      emailNode(
        "4",
        "Send Personalized Feedback Survey",
        760,
        "{guest_email}",
        "How was your {visit_type} experience, {guest_name}?",
        "<h2>{survey_intro}</h2><p>{survey_questions}</p><p>{nps_question}</p><p>{closing_message}</p><p><a href='https://feedback.example.com/survey?{survey_link_params}'>Take the Survey (2 minutes)</a></p>",
      ),

      // 5. Delay 3 days
      n("5", "delayAction", "Wait 3 Days for Survey Response", 1000, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 6. AI response analysis
      aiNode(
        "6",
        "AI Analyze Feedback Responses",
        1240,
        "You are a customer experience analyst. Analyze the survey response data to determine overall satisfaction, identify service issues, calculate sentiment score (0-100), and determine if service recovery is needed. Respond JSON: {satisfaction_rating: number, sentiment_score: number, positive_highlights: string, negative_issues: string, needs_recovery: string, recovery_reason: string, improvement_suggestions: string, nps_score: number}",
        "Guest: {guest_name} ({loyalty_tier}). Visit: {visit_type}. Services: {services_used}. Survey questions: {survey_questions}. Analyze the feedback and determine service recovery needs.",
        [
          {
            field: "satisfaction_rating",
            as: "satisfaction_rating",
            type: "number",
          },
          { field: "sentiment_score", as: "sentiment_score", type: "number" },
          {
            field: "positive_highlights",
            as: "positive_highlights",
            type: "string",
          },
          { field: "negative_issues", as: "negative_issues", type: "string" },
          { field: "needs_recovery", as: "needs_recovery", type: "string" },
          { field: "recovery_reason", as: "recovery_reason", type: "string" },
          {
            field: "improvement_suggestions",
            as: "improvement_suggestions",
            type: "string",
          },
          { field: "nps_score", as: "nps_score", type: "number" },
        ],
      ),

      // 7. Service recovery condition
      n("7", "condition", "Service Recovery Needed?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "needs_recovery", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 8. Slack alert (True path — recovery needed)
      slackNode(
        "8",
        "Alert Management for Service Recovery",
        1720,
        "SERVICE RECOVERY NEEDED:\\n*Guest:* {guest_name} ({loyalty_tier})\\n*Visit:* {visit_type} on {visit_date}\\n*Satisfaction:* {satisfaction_rating}/10 | Sentiment: {sentiment_score}/100 | NPS: {nps_score}\\n*Issues:* {negative_issues}\\n*Recovery Reason:* {recovery_reason}\\n*Suggestion:* {improvement_suggestions}\\n*Services Used:* {services_used} | Spend: {spend_amount}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1344. IT EQUIPMENT REQUEST (8 nodes)
  // ===================================================================

  "IT Equipment Request": {
    summary:
      "Receives equipment requests via webhook, extracts request data with AI, validates budget and policy with runCode, routes to manager for approval via email, waits 3 days, branches on approval status, checks inventory via API, and sends fulfillment notification to the employee.",
    nodes: [
      trigger("Webhook: Equipment request submitted", "webhook_app", {
        source: "it_portal",
        event: "equipment.request_submitted",
        howItStarts:
          "Fires when an employee submits an IT equipment request through the self-service portal, including item details, justification, and budget information",
        setup:
          "Configure webhook from your IT service portal (ServiceNow, Jira Service Management, or custom). Set OpenAI API key. Configure email for approval routing. Set inventory API endpoint. Provide Google Sheets for tracking.",
        inputFields: [],
      }),

      // 2. AI extract request data
      aiNode(
        "2",
        "AI Extract Equipment Request Details",
        280,
        "You are an IT procurement assistant. Extract the equipment request details: employee name, email, department, manager email, requested item, specifications, quantity, estimated cost, business justification, and urgency level. Respond JSON: {employee_name: string, employee_email: string, department: string, manager_email: string, item_name: string, item_specs: string, quantity: number, estimated_cost: number, justification: string, urgency: string, request_category: string}",
        "Raw webhook payload: {webhook_payload}. Parse all equipment request details regardless of field naming.",
        [
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
          { field: "item_name", as: "item_name", type: "string" },
          { field: "item_specs", as: "item_specs", type: "string" },
          { field: "quantity", as: "quantity", type: "number" },
          { field: "estimated_cost", as: "estimated_cost", type: "number" },
          { field: "justification", as: "justification", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "request_category", as: "request_category", type: "string" },
        ],
      ),

      // 3. Budget and policy check runCode
      n("3", "runCode", "Validate Budget and Policy Compliance", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check if the request falls within budget and policy limits
const cost = parseFloat(vars.estimated_cost || "0");
const quantity = parseInt(vars.quantity || "1", 10);
const totalCost = cost * quantity;
const category = (vars.request_category || "general").toLowerCase();
const urgency = (vars.urgency || "normal").toLowerCase();
// Policy limits by category
const policyLimits = { laptop: 2500, monitor: 800, keyboard: 200, mouse: 100, headset: 300, software: 1000, general: 500 };
const categoryLimit = policyLimits[category] || policyLimits.general;
const withinPolicy = totalCost <= categoryLimit;
const needsDirectorApproval = totalCost > 1000;
const autoApprovable = totalCost <= 200 && !needsDirectorApproval;
const requestId = "EQ-" + Date.now().toString(36).toUpperCase();
return {
  request_id: requestId,
  total_cost: totalCost,
  category_limit: categoryLimit,
  within_policy: withinPolicy ? "yes" : "no",
  needs_director_approval: needsDirectorApproval ? "yes" : "no",
  auto_approvable: autoApprovable ? "yes" : "no",
  approval_level: autoApprovable ? "auto" : needsDirectorApproval ? "director" : "manager",
  policy_note: withinPolicy ? "Within " + category + " budget limit ($" + categoryLimit + ")" : "Exceeds " + category + " limit ($" + categoryLimit + ") — requires exception approval",
  submitted_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "request_id", type: "string" },
            { name: "total_cost", type: "number" },
            { name: "category_limit", type: "number" },
            { name: "within_policy", type: "string" },
            { name: "needs_director_approval", type: "string" },
            { name: "auto_approvable", type: "string" },
            { name: "approval_level", type: "string" },
            { name: "policy_note", type: "string" },
            { name: "submitted_at", type: "string" },
          ],
        },
      }),

      // 4. Manager approval routing email
      emailNode(
        "4",
        "Send Approval Request to Manager",
        760,
        "{manager_email}",
        "Equipment Request Approval — {request_id}: {item_name} for {employee_name}",
        "<h2>Equipment Request for Approval</h2><p><strong>Request ID:</strong> {request_id}</p><p><strong>Employee:</strong> {employee_name} ({department})</p><p><strong>Item:</strong> {item_name} — {item_specs}</p><p><strong>Quantity:</strong> {quantity} | <strong>Total Cost:</strong> ${total_cost}</p><p><strong>Urgency:</strong> {urgency}</p><p><strong>Justification:</strong> {justification}</p><hr><p><strong>Policy Check:</strong> {policy_note}</p><p><strong>Approval Level:</strong> {approval_level}</p><p>Please reply APPROVED or DENIED with any notes.</p>",
      ),

      // 5. Delay 3 days
      n("5", "delayAction", "Wait 3 Days for Manager Approval", 1000, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 6. Approval branch condition
      n("6", "condition", "Request Approved by Manager?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "within_policy", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 7. Inventory check API (True path — approved)
      n("7", "hitApi", "Check Equipment Inventory", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ITInventory!A2:E200",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "inventory_data", type: "string" },
            { name: "range", customName: "inventory_range", type: "string" },
          ],
        },
      }),

      // 8. Employee notification email
      emailNode(
        "8",
        "Notify Employee of Request Status",
        1720,
        "{employee_email}",
        "Equipment Request Update — {request_id}: {item_name}",
        "<h2>Equipment Request Update</h2><p>Hi {employee_name},</p><p>Your equipment request <strong>{request_id}</strong> has been processed:</p><ul><li><strong>Item:</strong> {item_name} ({item_specs})</li><li><strong>Quantity:</strong> {quantity}</li><li><strong>Total Cost:</strong> ${total_cost}</li><li><strong>Policy Status:</strong> {policy_note}</li><li><strong>Approval Level:</strong> {approval_level}</li></ul><p>Our IT team will coordinate delivery once the item is confirmed in stock. You will receive a follow-up with tracking details.</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1345. IT EQUIPMENT REQUEST BOT (8 nodes)
  // ===================================================================

  "IT Equipment Request Bot": {
    summary:
      "Receives chat-based equipment requests via webhook, uses AI for needs assessment, looks up the equipment catalog via API, formalizes the request with AI, routes for automated approval via email, waits 2 days, processes the approval decision via condition, and coordinates fulfillment through Slack.",
    nodes: [
      trigger("Webhook: Chat-based equipment request", "webhook_app", {
        source: "chat_platform",
        event: "equipment.chat_request",
        howItStarts:
          "Fires when an employee sends a chat message requesting IT equipment through Slack, Teams, or an internal chatbot interface",
        setup:
          "Configure webhook from your chat platform (Slack Events API, Teams webhook, or chatbot). Set OpenAI API key for conversational understanding. Provide equipment catalog API or Sheets. Configure email for approval routing. Set Slack webhook for fulfillment.",
        inputFields: [],
      }),

      // 2. AI needs assessment
      aiNode(
        "2",
        "AI Assess Equipment Needs from Chat",
        280,
        "You are a friendly IT helpdesk bot. Parse the chat message to understand what equipment the person needs, why they need it, and their urgency. Extract their identity, the requested items, and any constraints. Respond JSON: {employee_name: string, employee_email: string, employee_id: string, requested_item: string, item_category: string, reason: string, urgency: string, budget_constraint: string, preferred_specs: string, department: string, manager_email: string}",
        "Raw chat webhook payload: {webhook_payload}. Understand the equipment need regardless of how casually the request is phrased.",
        [
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "employee_id", as: "employee_id", type: "string" },
          { field: "requested_item", as: "requested_item", type: "string" },
          { field: "item_category", as: "item_category", type: "string" },
          { field: "reason", as: "request_reason", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          {
            field: "budget_constraint",
            as: "budget_constraint",
            type: "string",
          },
          { field: "preferred_specs", as: "preferred_specs", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
        ],
      ),

      // 3. Catalog lookup API
      n("3", "hitApi", "Search Equipment Catalog", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/EquipmentCatalog!A2:G100",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "catalog_items", type: "string" },
            { name: "range", customName: "catalog_range", type: "string" },
          ],
        },
      }),

      // 4. AI request formalization
      aiNode(
        "4",
        "AI Formalize Equipment Request",
        760,
        "You are an IT procurement specialist. Based on the employee's informal request and the equipment catalog, create a formal equipment request. Match the best catalog item to their needs, including SKU, price, and availability. Respond JSON: {formal_request_title: string, matched_item: string, item_sku: string, item_price: number, in_stock: string, alternative_option: string, delivery_estimate: string, total_with_tax: number, request_summary: string}",
        "Employee: {employee_name} ({department}). Request: {requested_item}. Reason: {request_reason}. Specs: {preferred_specs}. Budget: {budget_constraint}. Urgency: {urgency}. Catalog: {catalog_items}.",
        [
          {
            field: "formal_request_title",
            as: "formal_request_title",
            type: "string",
          },
          { field: "matched_item", as: "matched_item", type: "string" },
          { field: "item_sku", as: "item_sku", type: "string" },
          { field: "item_price", as: "item_price", type: "number" },
          { field: "in_stock", as: "in_stock", type: "string" },
          {
            field: "alternative_option",
            as: "alternative_option",
            type: "string",
          },
          {
            field: "delivery_estimate",
            as: "delivery_estimate",
            type: "string",
          },
          { field: "total_with_tax", as: "total_with_tax", type: "number" },
          { field: "request_summary", as: "request_summary", type: "string" },
        ],
      ),

      // 5. Automated approval routing email
      emailNode(
        "5",
        "Route Equipment Request for Approval",
        1000,
        "{manager_email}",
        "Equipment Approval: {formal_request_title} for {employee_name}",
        "<h2>Equipment Request for Approval</h2><p><strong>Employee:</strong> {employee_name} ({department})</p><p><strong>Request:</strong> {formal_request_title}</p><p><strong>Item:</strong> {matched_item} (SKU: {item_sku})</p><p><strong>Price:</strong> ${item_price} (total w/tax: ${total_with_tax})</p><p><strong>In Stock:</strong> {in_stock} | <strong>Delivery:</strong> {delivery_estimate}</p><p><strong>Reason:</strong> {request_reason}</p><p><strong>Urgency:</strong> {urgency}</p><p><strong>Summary:</strong> {request_summary}</p><hr><p>Reply APPROVED or DENIED with notes.</p>",
      ),

      // 6. Delay 2 days
      n("6", "delayAction", "Wait 2 Days for Approval", 1240, {
        delayDetails: { duration: 2, unit: "days" },
      }),

      // 7. Approval processing condition
      n("7", "condition", "Equipment In Stock for Fulfillment?", 1480, {
        conditionDetails: {
          conditions: [{ variable: "in_stock", operator: "eq", value: "yes" }],
        },
      }),

      // 8. Fulfillment coordination Slack (True path)
      slackNode(
        "8",
        "Coordinate Equipment Fulfillment via Slack",
        1720,
        "EQUIPMENT FULFILLMENT REQUEST:\\n*Employee:* {employee_name} ({department})\\n*Item:* {matched_item} (SKU: {item_sku})\\n*Price:* ${total_with_tax}\\n*Delivery Estimate:* {delivery_estimate}\\n*Reason:* {request_reason}\\n*Urgency:* {urgency}\\nPlease prepare for shipping to {employee_name}.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1346. IMPACT REPORT GENERATOR (8 nodes)
  // ===================================================================

  "Impact Report Generator": {
    summary:
      "Runs on a schedule to collect multi-source data with runCode, calculates outcome metrics with runCode, generates an AI narrative, creates visualizations with runCode, compiles the report with runCode, produces stakeholder-specific versions via email, and tracks distribution in Sheets.",
    nodes: [
      trigger("Schedule: Monthly impact report generation", "schedule", {
        schedule: "1st of every month at 7:00 AM",
        howItStarts:
          "Runs monthly to generate a comprehensive impact report by collecting data from multiple sources, calculating outcome metrics, and producing stakeholder-ready reports",
        setup:
          "Configure data source API endpoints (CRM, analytics, finance). Set OpenAI API key for narrative generation. Configure email for report distribution. Set Google Sheets for data collection and distribution tracking.",
        inputFields: [],
      }),

      // 2. Multi-source data collection runCode
      n("2", "runCode", "Collect Data from Multiple Sources", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate data from multiple sources for impact reporting
const now = new Date();
const reportMonth = now.toLocaleDateString("en-US", { month: "long", year: "numeric" });
const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
const periodStart = lastMonth.toISOString().split("T")[0];
const periodEnd = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split("T")[0];
// Simulated data aggregation (in production, these would come from API calls)
const metrics = {
  total_beneficiaries: 1250,
  new_beneficiaries: 180,
  programs_active: 12,
  events_held: 8,
  volunteer_hours: 3400,
  funds_raised: 145000,
  funds_disbursed: 128000,
  satisfaction_score: 4.2,
  completion_rate: 87,
  partner_organizations: 15
};
return {
  report_month: reportMonth,
  period_start: periodStart,
  period_end: periodEnd,
  total_beneficiaries: metrics.total_beneficiaries,
  new_beneficiaries: metrics.new_beneficiaries,
  programs_active: metrics.programs_active,
  events_held: metrics.events_held,
  volunteer_hours: metrics.volunteer_hours,
  funds_raised: metrics.funds_raised,
  funds_disbursed: metrics.funds_disbursed,
  satisfaction_score: metrics.satisfaction_score,
  completion_rate: metrics.completion_rate,
  partner_count: metrics.partner_organizations,
  data_collected_at: now.toISOString()
};`,
          outputVariables: [
            { name: "report_month", type: "string" },
            { name: "period_start", type: "string" },
            { name: "period_end", type: "string" },
            { name: "total_beneficiaries", type: "number" },
            { name: "new_beneficiaries", type: "number" },
            { name: "programs_active", type: "number" },
            { name: "events_held", type: "number" },
            { name: "volunteer_hours", type: "number" },
            { name: "funds_raised", type: "number" },
            { name: "funds_disbursed", type: "number" },
            { name: "satisfaction_score", type: "number" },
            { name: "completion_rate", type: "number" },
            { name: "partner_count", type: "number" },
            { name: "data_collected_at", type: "string" },
          ],
        },
      }),

      // 3. Outcome metric calculation runCode
      n("3", "runCode", "Calculate Outcome Metrics and KPIs", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate derived KPIs and outcome metrics
const raised = parseInt(vars.funds_raised || "0", 10);
const disbursed = parseInt(vars.funds_disbursed || "0", 10);
const beneficiaries = parseInt(vars.total_beneficiaries || "0", 10);
const newBen = parseInt(vars.new_beneficiaries || "0", 10);
const hours = parseInt(vars.volunteer_hours || "0", 10);
const programs = parseInt(vars.programs_active || "0", 10);
const costPerBeneficiary = beneficiaries > 0 ? Math.round(disbursed / beneficiaries) : 0;
const growthRate = beneficiaries > 0 ? ((newBen / beneficiaries) * 100).toFixed(1) : "0";
const fundUtilization = raised > 0 ? ((disbursed / raised) * 100).toFixed(1) : "0";
const volunteerValue = hours * 29; // Independent Sector volunteer value rate
const costEfficiency = disbursed > 0 ? ((beneficiaries / disbursed) * 1000).toFixed(2) : "0";
const avgPerProgram = programs > 0 ? Math.round(beneficiaries / programs) : 0;
return {
  cost_per_beneficiary: costPerBeneficiary,
  growth_rate: growthRate,
  fund_utilization: fundUtilization,
  volunteer_value: volunteerValue,
  cost_efficiency: costEfficiency,
  avg_per_program: avgPerProgram,
  surplus_deficit: raised - disbursed,
  kpi_summary: "Cost/beneficiary: $" + costPerBeneficiary + " | Growth: " + growthRate + "% | Utilization: " + fundUtilization + "% | Vol. Value: $" + volunteerValue.toLocaleString()
};`,
          outputVariables: [
            { name: "cost_per_beneficiary", type: "number" },
            { name: "growth_rate", type: "string" },
            { name: "fund_utilization", type: "string" },
            { name: "volunteer_value", type: "number" },
            { name: "cost_efficiency", type: "string" },
            { name: "avg_per_program", type: "number" },
            { name: "surplus_deficit", type: "number" },
            { name: "kpi_summary", type: "string" },
          ],
        },
      }),

      // 4. AI narrative generation
      aiNode(
        "4",
        "AI Generate Impact Narrative",
        760,
        "You are a nonprofit impact report writer. Create a compelling narrative for the monthly impact report. Highlight achievements, tell the story behind the numbers, acknowledge challenges, and outline future goals. Write in a professional but inspiring tone. Respond JSON: {executive_summary: string, key_achievements: string, beneficiary_stories: string, challenges_faced: string, future_outlook: string, call_to_action: string}",
        "Month: {report_month}. Beneficiaries: {total_beneficiaries} (+{new_beneficiaries} new). Programs: {programs_active}. Events: {events_held}. Volunteer hours: {volunteer_hours} (value: ${volunteer_value}). Funds raised: ${funds_raised}. Disbursed: ${funds_disbursed}. Satisfaction: {satisfaction_score}/5. Completion: {completion_rate}%. Partners: {partner_count}. KPIs: {kpi_summary}.",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "key_achievements", as: "key_achievements", type: "string" },
          {
            field: "beneficiary_stories",
            as: "beneficiary_stories",
            type: "string",
          },
          { field: "challenges_faced", as: "challenges_faced", type: "string" },
          { field: "future_outlook", as: "future_outlook", type: "string" },
          { field: "call_to_action", as: "call_to_action", type: "string" },
        ],
      ),

      // 5. Visualization creation runCode
      n("5", "runCode", "Generate Report Data Visualizations", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate ASCII/text-based data visualizations for the report
const maxBar = 20;
const raised = parseInt(vars.funds_raised || "0", 10);
const disbursed = parseInt(vars.funds_disbursed || "0", 10);
const maxFund = Math.max(raised, disbursed);
const raisedBar = Math.round((raised / maxFund) * maxBar);
const disbursedBar = Math.round((disbursed / maxFund) * maxBar);
const fundChart = "Raised:    [" + "=".repeat(raisedBar) + "] $" + raised.toLocaleString() +
  "\\nDisbursed: [" + "=".repeat(disbursedBar) + "] $" + disbursed.toLocaleString();
const satisfactionStars = "★".repeat(Math.round(parseFloat(vars.satisfaction_score || "0"))) + "☆".repeat(5 - Math.round(parseFloat(vars.satisfaction_score || "0")));
const completionBar = Math.round(parseInt(vars.completion_rate || "0", 10) / 5);
const completionChart = "[" + "█".repeat(completionBar) + "░".repeat(20 - completionBar) + "] " + vars.completion_rate + "%";
return {
  fund_chart: fundChart,
  satisfaction_visual: satisfactionStars + " (" + vars.satisfaction_score + "/5)",
  completion_visual: completionChart,
  visualizations_ready: "yes"
};`,
          outputVariables: [
            { name: "fund_chart", type: "string" },
            { name: "satisfaction_visual", type: "string" },
            { name: "completion_visual", type: "string" },
            { name: "visualizations_ready", type: "string" },
          ],
        },
      }),

      // 6. Report compilation runCode
      n("6", "runCode", "Compile Full Impact Report", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compile the full impact report HTML
const report = "<h1>Impact Report — " + vars.report_month + "</h1>" +
  "<h2>Executive Summary</h2><p>" + (vars.executive_summary || "") + "</p>" +
  "<h2>Key Metrics</h2>" +
  "<table border='1' cellpadding='8'>" +
  "<tr><td>Total Beneficiaries</td><td>" + vars.total_beneficiaries + " (+" + vars.new_beneficiaries + " new)</td></tr>" +
  "<tr><td>Active Programs</td><td>" + vars.programs_active + "</td></tr>" +
  "<tr><td>Events Held</td><td>" + vars.events_held + "</td></tr>" +
  "<tr><td>Volunteer Hours</td><td>" + vars.volunteer_hours + " (valued at $" + vars.volunteer_value + ")</td></tr>" +
  "<tr><td>Funds Raised</td><td>$" + vars.funds_raised + "</td></tr>" +
  "<tr><td>Funds Disbursed</td><td>$" + vars.funds_disbursed + "</td></tr>" +
  "<tr><td>Satisfaction</td><td>" + vars.satisfaction_visual + "</td></tr>" +
  "<tr><td>Completion Rate</td><td>" + vars.completion_visual + "</td></tr>" +
  "<tr><td>Cost per Beneficiary</td><td>$" + vars.cost_per_beneficiary + "</td></tr>" +
  "<tr><td>Fund Utilization</td><td>" + vars.fund_utilization + "%</td></tr>" +
  "</table>" +
  "<h2>Key Achievements</h2><p>" + (vars.key_achievements || "") + "</p>" +
  "<h2>Beneficiary Impact</h2><p>" + (vars.beneficiary_stories || "") + "</p>" +
  "<h2>Challenges</h2><p>" + (vars.challenges_faced || "") + "</p>" +
  "<h2>Looking Ahead</h2><p>" + (vars.future_outlook || "") + "</p>" +
  "<h2>How You Can Help</h2><p>" + (vars.call_to_action || "") + "</p>";
return {
  full_report_html: report,
  report_compiled: "yes",
  report_word_count: report.split(/\\s+/).length
};`,
          outputVariables: [
            { name: "full_report_html", type: "string" },
            { name: "report_compiled", type: "string" },
            { name: "report_word_count", type: "number" },
          ],
        },
      }),

      // 7. Stakeholder-specific versions email
      emailNode(
        "7",
        "Send Impact Report to Stakeholders",
        1480,
        "{stakeholder_distribution_list}",
        "Monthly Impact Report — {report_month}",
        "{full_report_html}",
      ),

      // 8. Distribution tracking Sheets
      sheetsNode(
        "8",
        "Log Report Distribution to Sheets",
        1720,
        "ImpactReports",
        '[["{data_collected_at}","{report_month}","{total_beneficiaries}","{funds_raised}","{funds_disbursed}","{fund_utilization}","{cost_per_beneficiary}","{satisfaction_score}","{completion_rate}","{report_word_count}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1347. INCIDENT ALERT WORKFLOW (9 nodes)
  // ===================================================================

  "Incident Alert Workflow": {
    summary:
      "Triggers on incident detection via webhook, extracts incident data with AI, classifies severity with runCode, routes based on severity level via condition, pages on-call engineers through Slack, creates a war room channel in Slack, updates the status page via API, communicates with affected customers by email, and tracks resolution in Sheets.",
    nodes: [
      trigger("Webhook: Incident detected", "webhook_app", {
        source: "monitoring_system",
        event: "incident.detected",
        howItStarts:
          "Fires when the monitoring system detects a service incident, including error details, affected services, alert source, and initial severity assessment",
        setup:
          "Configure webhook from your monitoring system (PagerDuty, Datadog, OpsGenie, or custom). Set OpenAI API key for incident analysis. Configure Slack for team communication. Set status page API. Configure email for customer notifications. Set Google Sheets for incident tracking.",
        inputFields: [],
      }),

      // 2. AI extract incident data
      aiNode(
        "2",
        "AI Extract Incident Details",
        280,
        "You are an incident management specialist. Extract all incident details from the monitoring alert: service affected, error type, error message, affected region, user impact estimate, alert source, initial severity, time of detection, and any diagnostic data. Respond JSON: {service_name: string, error_type: string, error_message: string, affected_region: string, user_impact_estimate: string, alert_source: string, initial_severity: string, detection_time: string, diagnostic_data: string, affected_endpoint: string, oncall_email: string}",
        "Raw monitoring webhook payload: {webhook_payload}. Extract all incident details regardless of alert format.",
        [
          { field: "service_name", as: "service_name", type: "string" },
          { field: "error_type", as: "error_type", type: "string" },
          { field: "error_message", as: "error_message", type: "string" },
          { field: "affected_region", as: "affected_region", type: "string" },
          {
            field: "user_impact_estimate",
            as: "user_impact_estimate",
            type: "string",
          },
          { field: "alert_source", as: "alert_source", type: "string" },
          { field: "initial_severity", as: "initial_severity", type: "string" },
          { field: "detection_time", as: "detection_time", type: "string" },
          { field: "diagnostic_data", as: "diagnostic_data", type: "string" },
          {
            field: "affected_endpoint",
            as: "affected_endpoint",
            type: "string",
          },
          { field: "oncall_email", as: "oncall_email", type: "string" },
        ],
      ),

      // 3. Incident classification runCode
      n("3", "runCode", "Classify Incident Severity and Priority", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify incident severity with structured criteria
const errorType = (vars.error_type || "").toLowerCase();
const severity = (vars.initial_severity || "medium").toLowerCase();
const impact = (vars.user_impact_estimate || "").toLowerCase();
const service = (vars.service_name || "").toLowerCase();
// Severity scoring
let severityScore = 0;
if (severity.includes("critical") || severity.includes("p1")) severityScore = 4;
else if (severity.includes("high") || severity.includes("p2")) severityScore = 3;
else if (severity.includes("medium") || severity.includes("p3")) severityScore = 2;
else severityScore = 1;
// Impact boost
if (impact.includes("all") || impact.includes("100%")) severityScore = Math.max(severityScore, 4);
else if (impact.includes("majority") || impact.includes("50%")) severityScore = Math.max(severityScore, 3);
// Core service boost
if (service.includes("auth") || service.includes("payment") || service.includes("database")) severityScore = Math.max(severityScore, 3);
const severityLabel = severityScore >= 4 ? "SEV1" : severityScore >= 3 ? "SEV2" : severityScore >= 2 ? "SEV3" : "SEV4";
const isCritical = severityScore >= 3 ? "yes" : "no";
const needsWarRoom = severityScore >= 3 ? "yes" : "no";
const needsStatusPage = severityScore >= 2 ? "yes" : "no";
const needsCustomerComm = severityScore >= 3 ? "yes" : "no";
const incidentId = "INC-" + Date.now().toString(36).toUpperCase();
return {
  incident_id: incidentId,
  severity_label: severityLabel,
  severity_score: severityScore,
  is_critical: isCritical,
  needs_war_room: needsWarRoom,
  needs_status_page: needsStatusPage,
  needs_customer_comm: needsCustomerComm,
  classification_time: new Date().toISOString()
};`,
          outputVariables: [
            { name: "incident_id", type: "string" },
            { name: "severity_label", type: "string" },
            { name: "severity_score", type: "number" },
            { name: "is_critical", type: "string" },
            { name: "needs_war_room", type: "string" },
            { name: "needs_status_page", type: "string" },
            { name: "needs_customer_comm", type: "string" },
            { name: "classification_time", type: "string" },
          ],
        },
      }),

      // 4. Severity-based routing condition
      n("4", "condition", "Critical Severity (SEV1/SEV2)?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "is_critical", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 5. On-call engineer paging Slack (True path)
      slackNode(
        "5",
        "Page On-Call Engineer via Slack",
        1000,
        "INCIDENT ALERT ({severity_label}) — {incident_id}:\\n*Service:* {service_name}\\n*Error:* {error_type} — {error_message}\\n*Region:* {affected_region}\\n*Endpoint:* {affected_endpoint}\\n*User Impact:* {user_impact_estimate}\\n*Detected:* {detection_time}\\n*Source:* {alert_source}\\n*Diagnostics:* {diagnostic_data}\\n@oncall Please acknowledge and begin investigation.",
      ),

      // 6. War room creation Slack
      slackNode(
        "6",
        "Create Incident War Room Channel",
        1240,
        "WAR ROOM CREATED for {incident_id} ({severity_label}):\\n*Service:* {service_name}\\n*Status:* Investigating\\n*Incident Commander:* {oncall_email}\\n*Join:* #incident-{incident_id}\\n\\nAll updates in this channel. Initial error: {error_message}",
        400,
      ),

      // 7. Status page update API
      n("7", "hitApi", "Update Status Page", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.statuspage.io/v1/pages/{statuspage_page_id}/incidents",
          headers: [
            { key: "Authorization", value: "OAuth {statuspage_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "incident[name]",
              value: "{severity_label}: {service_name} degraded performance",
            },
            { key: "incident[status]", value: "investigating" },
            { key: "incident[impact_override]", value: "{severity_label}" },
            {
              key: "incident[body]",
              value:
                "We are investigating reports of {error_type} affecting {service_name} in {affected_region}. Impact: {user_impact_estimate}. We will provide updates as they become available.",
            },
          ],
          variables: [
            {
              name: "id",
              customName: "statuspage_incident_id",
              type: "string",
            },
            { name: "status", customName: "statuspage_status", type: "string" },
            {
              name: "shortlink",
              customName: "statuspage_link",
              type: "string",
            },
          ],
        },
      }),

      // 8. Customer communication email
      emailNode(
        "8",
        "Notify Affected Customers",
        1720,
        "{customer_notification_list}",
        "Service Notice: {service_name} — {severity_label} Incident",
        "<h2>Service Status Update</h2><p>We are currently experiencing issues with <strong>{service_name}</strong> that may affect your experience.</p><h3>What's happening</h3><p>Our monitoring systems detected {error_type} affecting services in {affected_region}. We estimate this impacts {user_impact_estimate}.</p><h3>What we're doing</h3><p>Our engineering team has been alerted and is actively investigating. Incident ID: {incident_id} ({severity_label}).</p><h3>Stay updated</h3><p>Follow our status page for real-time updates: {statuspage_link}</p><p>We apologize for the inconvenience and will resolve this as quickly as possible.</p>",
      ),

      // 9. Resolution tracking Sheets
      sheetsNode(
        "9",
        "Log Incident to Resolution Tracker",
        1960,
        "IncidentTracker",
        '[["{classification_time}","{incident_id}","{severity_label}","{service_name}","{error_type}","{affected_region}","{user_impact_estimate}","{alert_source}","{statuspage_incident_id}","investigating","{oncall_email}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "9", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },
};
